import React, { useEffect, useRef, useState ,useLayoutEffect, useCallback  } from 'react';
import configDetails from '../Config/Config';
import { useNavigate } from 'react-router-dom';
import routesfe from '../Config/RoutesFE';
import { useRecord } from '../components copy/RecordContext';
import {useSessionRestore} from '../hooks/useSessionRestore';
import { useWebSocketConnection } from '../hooks/useWebSocketConnection';
import { isAutoDeleteEnabled } from '../utils/AutoDeleteUtils';
import { handleImageUpload, batchConvertS3UrisToPresignedUrls } from '../utils/imageUtils';
import {getCookie,setCookie,deleteCookie,setLiveSessionCookies,clearLiveSessionCookies,saveRecordingStartTime,saveRecordingStatus,savePauseState,calculateElapsedTime,
  getSessionStateFromCookies,
  clearSessionState,
  saveTimerToStorage,
} from '../utils/cookieUtils';
import {storeConfigurationResponse, getStoredModel,getStoredToken,normalizeTranscriptMessage} from '../utils/tokenUtils';
import {sendSegmentToViewers,getFormattedTimestamp,getDisplaySegments,createSegment} from '../utils/segmentUtils';
import {putFileToS3,setupS3AudioRecording,stopAssemblyStream,startTranscriptionStream,createTranscriptionSocket,initializeAudioStream,setupWorkletMessageHandler,setupTranscriptionSocketHandlers,setupTranscriptMessageHandler,flushAudioQueue,handleAssemblyDisconnection, forceAssemblyReconnect,startTranscriptionWatchdog,stopTranscriptionWatchdog} from '../hooks/useAudioSetup';
import { reprocessMessage} from '../utils/reprocessUtils';
import { applyPrevDivCorrection } from '../utils/sentenceBoundaryUtils';
import useReprocessControl from '../hooks/useReprocessControl';
import { Auth } from 'aws-amplify';
import { Form } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { reconnectWebSocket } from '../utils/reconnectWebSocket';
import {
  WS_ERROR_MESSAGES,extractResponseCode,isPlanLimitErrorCode,normalizeWarningPayload,handleWarningToastAndCountdown,isNearTimeLimitExpiry,usePlanLimitControls,useTimeLimitDisconnect,
} from '../utils/wsErrorCodes';
import {
  BsMicFill,
  BsClockFill,
  BsPersonFill,
  BsTagsFill,
  BsPauseCircleFill,
  BsPauseFill,
  BsStopFill,
  BsRecordCircleFill,
  BsShareFill
} from 'react-icons/bs';
import logger from '../utils/Logger';
import ShareModal from './ShareModal';
import ManualTextModal from './ManualTextModal';
import SubscriptionExpiredModal, { checkSubscriptionExpired } from './SubscriptionExpiredModal';
import DOMPurify from "dompurify";
import '../Styles/Record.css';
import { startAudioS3Recording, stopAudioS3Recording , pauseAudioRecording, resumeAudioRecording, getRecordedAudioDurationS } from '../utils/audioS3Manager';
import { convertWebmToWav } from '../utils/audioConversionUtils';

// Expose refs globally for access by TopNavbar
if (typeof window !== 'undefined') {
  window.assemblySocketRef = null;
  window.workletNodeRef = null;
  window.audioContextRef = null;
  window.streamRef = null;
  window.mediaRecorderRef = null;
  
}

const {
  websocketBaseUrl,
  baseUrl,
  assemblyWsBaseUrl,
  deepgramWsBaseUrl,
  trackingUrl,
  INSERT_MESSAGE,
  CONVERSATION_INITIATED,
  CONVERSATION_COMPLETED,
  TRACKING_TIME,
  META_DATA,
  REPROCESS,
  sendAudioChunk,
  TRANSCRIPT
} = configDetails;

// S3 Audio Upload API URL
const S3_AUDIO_UPLOAD_URL = `${baseUrl}${configDetails.UPLOAD_AUDIO}`;

function getAuthenticatedUserId() {
  const userData = localStorage.getItem('user');
  if (userData) {
    try {
      const user = JSON.parse(userData);
      const userId = user.userSub;
      return userId;
    } catch (error) {
      logger.error('Failed to parse user data for authentication', {
        component: 'Record',
        action: 'USER_DATA_PARSE_FAILED',
        error
      });
      console.error('❌ [getAuthenticatedUserId] Error parsing user data:', error);
    }
  }
  return null;
}

function getUserName() {
  const userData = localStorage.getItem('user');
  if (userData) {
    try {
      const user = JSON.parse(userData);
      const name = user.name || user.email || 'User';
      return name;
    } catch (error) {
      logger.error('Failed to parse user data for name', {
        component: 'Record',
        action: 'USER_NAME_PARSE_FAILED',
        error
      });
      console.error('❌ [getUserName] Error parsing user data:', error);
    }
  }
  return 'User';
}

// Helper function to get authIdToken from AWS Amplify
async function getAuthIdToken() {
  try {
    const currentUser = await Auth.currentAuthenticatedUser();
    const token = currentUser.signInUserSession.idToken.jwtToken;
    return token;
  } catch (error) {
    console.error('❌ [getAuthIdToken] Error getting auth token:', error);
    return null;
  }
}

// Helper to generate random 4-letter alpha string
const generateRandomAlpha = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  let result = '';
  for (let i = 0; i < 4; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const COMPONENT_NAME = 'Record';

function Record({ authIdToken }) {
  // Log component mount
  useEffect(() => {
    const userId = getAuthenticatedUserId();
    const urlConvId = window.location.pathname.split('/').pop();
    const hasConversationInUrl = urlConvId && urlConvId !== 'record' && urlConvId.length > 10;
    
    logger.info('Record component mounted', {
      component: COMPONENT_NAME,
      action: 'COMPONENT_MOUNT',
      userId,
      context: {
        hasAuthToken: !!authIdToken,
        pathname: window.location.pathname,
        hasConversationInUrl,
        conversationIdFromUrl: hasConversationInUrl ? urlConvId : null,
        existingSessionId: getCookie('session_id') || null,
        existingConversationId: getCookie('conversation_id') || null,
        isLiveSession: getCookie('isLiveSession') === 'true',
        recordingStatus: getCookie('recording_status') || null,
        browserOnline: navigator.onLine,
        timestamp: Date.now()
      },
      includeBrowserInfo: true
    });
    
    return () => {
      logger.info('Record component unmounted', {
        component: COMPONENT_NAME,
        action: 'COMPONENT_UNMOUNT',
        userId: getAuthenticatedUserId(),
        context: {
          isLive,
          isPaused,
          timer,
          conversationId: conversationId || getCookie('conversation_id'),
          sessionId: sessionId || getCookie('session_id'),
          transcriptSegmentsCount: transcriptSegments?.length || 0
        }
      });
    };
  }, []);
  // On mount, if sessionId is null, set it from 'originalsessionid' or 'sessionid' cookie (matching actual cookie names)
  useEffect(() => {
    if (!sessionId) {
      const originalSessionId = getCookie('original_session_id');
      const fallbackSessionId = getCookie('session_id');
      if (originalSessionId) {
        setSessionId(originalSessionId);
        logger.debug('Session ID restored from original_session_id cookie', {
          component: COMPONENT_NAME,
          action: 'SESSION_RESTORE',
          userId: getAuthenticatedUserId(),
          context: { sessionId: originalSessionId }
        });
      } else if (fallbackSessionId) {
        setSessionId(fallbackSessionId);
        logger.debug('Session ID restored from session_id cookie', {
          component: COMPONENT_NAME,
          action: 'SESSION_RESTORE',
          userId: getAuthenticatedUserId(),
          context: { sessionId: fallbackSessionId }
        });
      }
    }
  }, []);
  // On cold start, do not clear isLiveSession cookie on refresh
  React.useEffect(() => {
    const isActive = getCookie('conversation_active') === 'true';
    const isLiveSession = getCookie('isLiveSession');
  }, []);
  React.useEffect(() => {
    window.handleStart = handleStart;
    return () => { delete window.handleStart; };
  }, []);
  // Add ref to store last known timer value during pause/reconnection
  const lastKnownTimerRef = useRef(0);
  // Track when reconnection started (so reconnection time doesn't advance timer)
  const reconnectStartedAtRef = useRef(null);
  const chunkAudioOffsetRef = useRef((() => {
    const savedAudioS = parseFloat(getCookie('recorded_audio_duration_s') ?? '0') || 0;
    const writtenAt = parseInt(getCookie('recorded_audio_duration_written_at') ?? '0', 10) || 0;
    const isFresh = savedAudioS > 0 && writtenAt > 0 && (Date.now() - writtenAt) < 5000;
    return isFresh ? savedAudioS : 0;
  })());

  const rafIdRef = useRef(null);
  const lastRenderedSecondRef = useRef(-1);

  useLayoutEffect(() => {
  const startTime = getCookie('recording_start_time');

  if (startTime) {
    const elapsed = calculateElapsedTime();
    lastKnownTimerRef.current = elapsed;
    setTimer(elapsed);
  }
}, []);
  // Add a state to track reconnection for effect dependency
  const [isReconnecting, setIsReconnecting] = useState(false);

  // Keep isReconnecting state in sync with ref
  useEffect(() => {
    const checkReconnectionState = () => {
      if (isReconnectingRef.current !== isReconnecting) {
        setIsReconnecting(isReconnectingRef.current);
      }
    };
    
    const interval = setInterval(checkReconnectionState, 200);
    return () => clearInterval(interval);
  }, [isReconnecting]);

  // Handle reconnection when user comes back online
  useEffect(() => {
    const handleOnline = () => {      
      const isLiveSessionCookie = getCookie('isLiveSession') === 'true';
      
      if (isLiveSessionCookie && !userInitiatedStopRef.current && !window.isStoppingSession) {
        // Check if WebSocket is not connected
        const wsState = socketRef.current?.readyState;
        
        if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
          setTimeout(() => {
            reconnectWebSocketLocal();
            
            // Also restart audio stream if needed
            setTimeout(() => {
              const assemblyState = assemblySocketRef.current?.readyState;
              
              if (!assemblySocketRef.current || assemblyState !== WebSocket.OPEN) {
                startTranscriptionStream({userInitiatedStopRef,assemblySocketRef,getStoredToken,startTranscriptionStreamWithToken,setStatus});
              } 
            }, 2000); // Wait for WebSocket to connect first
          }, 1000); // Small delay to ensure network is stable
        }
      } 
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  const userInitiatedStopRef = useRef(false);
  const saveInProgressRef = useRef(false);
  const lastTranscriptTimeRef = useRef(Date.now());
  const reconnectTimeoutRef = useRef(null);
  const speakingRef = useRef(false);
  const hasInitializedConnectionRef = useRef(false);

  const audioWorkletActiveRef = useRef(false);
  // Transcription-path telemetry refs (watchdog inputs)
  const lastAudioSentRef = useRef(0);        // Date.now() of last successful send to Deepgram
  const lastMicActivityRef = useRef(0);      // Date.now() of last worklet-produced audio
  const lastAudioDroppedRef = useRef(null);  // last queue-overflow drop info
  const droppedAudioTotalMsRef = useRef(0);  // cumulative dropped-audio duration (ms)
  const transcriptionWatchdogRef = useRef(null);
  // Timer for audio worklet activity check
  const audioWorkletTimeoutRef = useRef(null);
  // Prevent duplicate WS connects for same key
  const wsConnectLatchRef = useRef({ key: null, inFlight: false }); 
  const pauseTimestampRef = useRef(null);
  const audioQueueRef = useRef([]);
  const keepAliveIntervalRef = useRef(null);
  const reconnectionLockRef = useRef(false);
  const isReconnectingRef = useRef(false);
  // Ref to track current status for socket handlers
  const statusRef = useRef('Ready');
  const audioInitializationRef = useRef(false);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (saveInProgressRef.current) {
        e.preventDefault();
        // Modern browsers display their own generic message, but returnValue must be set.
        e.returnValue = 'Your recording is still being saved. If you leave now, your note will NOT be saved.';
        return e.returnValue;
      }
      try {
        const audioS = getRecordedAudioDurationS();
        if (audioS > 0) {
          setCookie('recorded_audio_duration_s', String(audioS));
          // Stamp the write time so startTranscriptionStreamWithToken can distinguish
          // a fresh post-refresh cookie from a stale pre-reconnect cookie.
          setCookie('recorded_audio_duration_written_at', String(Date.now()));
        }
      } catch (_) {}
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Safety: clear the flag on unmount so a stale ref can't keep the guard active.
      saveInProgressRef.current = false;
    };
  }, []);
  const {
    status, setStatus,
    sessionId, setSessionId,
    shareLink, setShareLink,
    transcriptSegments, setTranscriptSegments,
    timer, setTimer,
    dateString, setDateString,
    timeString, setTimeString,
    isPaused, setIsPaused,
    audioContextRef, streamRef, workletNodeRef,
    assemblySocketRef, socketRef, timerRef,
    waveformRef, analyserRef, animationFrameRef,
    isPausedRef, mediaRecorderRef, audioChunksRef,
    isLive,
    persistentSegments,
    setPersistentSegments,
    paginationMeta,
    setPaginationMeta,
    handleStopRef,
    resetForNewSession,
    resetAfterSessionCompleted,
    previousConversationId,
    setPreviousConversationId,
    conversationId,
    setConversationId,
    planCountdownEndsAt,
    setPlanCountdownEndsAt,
  } = useRecord();

  // Reprocess control hook
  // Reprocess control hook
  const { reprocessEnabled, reprocessEnabledRef, toggleReprocess } = useReprocessControl({
    enableByDefault: false,
  });

  // Paragraph breaking configuration (env vars with sensible defaults)
  const PARAGRAPH_BREAK_SECONDS = parseInt(process.env.REACT_APP_PARAGRAPH_BREAK_SECONDS, 10) || 5;
  const MAX_WORDS_PER_DIV = parseInt(process.env.REACT_APP_MAX_WORDS_PER_DIV, 10) || 200;

  useEffect(() => {
  if (isLive && transcriptSegments.length === 0) {
    setTimeout(() => {}, 0);
  }
}, [isLive]);


const isLiveSessionCookie = getCookie("isLiveSession") === "true";
const hideStartButton = isLiveSessionCookie && !isLive;
const isReconnectingUi = isLiveSessionCookie && !isLive;

  // Expose handleStop to context for external control (TopNavbar)
  useEffect(() => {
    if (handleStopRef) {
      handleStopRef.current = handleStop;
    }
    // Cleanup on unmount
    return () => {
      if (handleStopRef) handleStopRef.current = null;
    };
  }, [handleStopRef]);

  // New state for keywords input
  const [keywordsInput, setKeywordsInput] = React.useState("");

  // Add this new state variable for conversation ID from URL
  const [conversationIdFromUrl, setConversationIdFromUrl] = useState(null);

  // State to track user login status for triggering effects
  const [userId, setUserId] = useState(null);

  // State for token expiry timer management
  const [tokenCheckTimer, setTokenCheckTimer] = useState(null);

  // State for page loading (refresh/navigation)
  const [isPageLoading, setIsPageLoading] = useState(true);

  // State for microphone loading modal
  const [showMicLoadingModal, setShowMicLoadingModal] = useState(false);
  const [loadingModalType, setLoadingModalType] = useState('starting'); // 'starting' or 'stopping'

   // New state for S3 audio upload
  const [s3AudioRecorder, setS3AudioRecorder] = useState(null);
  const [s3AudioChunks, setS3AudioChunks] = useState([]);

  // Compute left sidebar width (desktop) to prevent footer overlap
  const [sidebarWidth, setSidebarWidth] = useState(0);
  useEffect(() => {
    const compute = () => {
      let width = 0;
      try {
        const isDesktop = window.matchMedia('(min-width: 769px)').matches;
        if (isDesktop) {
          const el = document.querySelector('.bg-white.border-end.vh-100') || document.querySelector('.bg-white.border-end');
          if (el) {
            const rect = (el).getBoundingClientRect();
            width = rect.width || 0;
          }
        }
      } catch {}
      setSidebarWidth(width);
    };
    compute();
    window.addEventListener('resize', compute);
    const el = document.querySelector('.bg-white.border-end.vh-100') || document.querySelector('.bg-white.border-end');
    let ro; let mo;
    if (el && 'ResizeObserver' in window) {
      ro = new ResizeObserver(compute);
      ro.observe(el);
    }
    if (el) {
      mo = new MutationObserver(compute);
      mo.observe(el, { attributes: true, attributeFilter: ['style', 'class'] });
    }
    return () => {
      window.removeEventListener('resize', compute);
      if (ro) ro.disconnect();
      if (mo) mo.disconnect();
    };
  }, []);

  const [s3UploadStatus, setS3UploadStatus] = useState('');
  const [s3Path, setS3Path] = useState(null); // Store S3 path after upload

  // State for manual text modal visibility
  const [showManualTextModal, setShowManualTextModal] = useState(false);
  
  // Handler for adding manual text from modal
  const handleAddManualTextModal = async (text, imageData) => {
    
    // If there's image data, add it to transcript first
    if (imageData) {
      // Create context object for image utilities
      const imageContext = {
        conversationId,
        conversationIdFromUrl,
        getCookie,
        getNewDivId,
        setCurrentDivId,
        currentDivIdRef,
        sequenceMapRef,
        setSequenceMap,
        setTranscriptSegments,
        socketRef,
        isPausedRef,
        getAuthenticatedUserId,
        calculateElapsedTime,
        sessionId
      };

      // Handle image upload using utility function
      await handleImageUpload(imageData, imageContext);
      
      // IMPORTANT: Return early to prevent any further processing
      return;
    }
    
    // Only process text if there's NO image data and text is valid
    if (text && text !== '<p><br></p>' && text.trim()) {
      // Additional check to make sure text doesn't contain base64 image data
      if (!text.includes('data:image/') && !text.includes('<img')) {
        handleAddManualText(text);
      }
    }
  };

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  // Subscription expired modal state
  const [showSubscriptionExpiredModal, setShowSubscriptionExpiredModal] = useState(false);
  const [subscriptionModalType, setSubscriptionModalType] = useState('subscription_expired');
  const navigate = useNavigate();

  // ── WS error-code plan-limit state, refs, and functions ───────────────────
  const {
    planWarningToast,planCountdown,planCountdownActive,planLimitStopInProgressRef,clearPlanCountdown,showPlanWarningToast,clearPlanWarningToast,handlePlanLimitReached,schedulePlanCountdownFromPayload,
  } = usePlanLimitControls({
    isPausedRef,mediaRecorderRef,userInitiatedStopRef,handleStop,clearLiveSessionCookies,clearSessionState,resetAfterSessionCompleted,navigate, planCountdownEndsAt,setPlanCountdownEndsAt,
    socketRef,
    getConversationId: () => conversationId || conversationIdFromUrl || getCookie('conversation_id'),
    getUserId: getAuthenticatedUserId,
    getTimer: () => lastKnownTimerRef?.current || timer || 0,
  });

  const { timeLimitExpiresAt, scheduleTimeLimitDisconnect } = useTimeLimitDisconnect({
    assemblySocketRef,conversationId,sessionId,getCookie,getAuthenticatedUserId,logger,COMPONENT_NAME,
  });

  // Format mm:ss for the footer display
  const formatPlanCountdown = (sec) => {
    if (sec === null || sec === undefined) return '';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  // State for paragraph tracking
  const [currentDivId, setCurrentDivId] = useState(null);
  const currentDivIdRef = useRef(null);
  // Track last paragraph start timestamp and div_id to prevent double div_id generation
  const lastParagraphTimestampRef = useRef(0);
  const lastParagraphDivIdRef = useRef(null);
  const paragraphStartTimerRef = useRef(0);
  // Keep currentDivIdRef in sync with currentDivId
  useEffect(() => {
    currentDivIdRef.current = currentDivId;
  }, [currentDivId]);
  const [sequenceMap, setSequenceMap] = useState({});
  const sequenceMapRef = useRef({});
  const lastSentTextRef = useRef({});

  // Helper: count words in a div from transcriptSegments and persistentSegments
  const countWordsInDiv = (divId) => {
    if (!divId) return 0;
    const gather = (arr) => (arr || []).filter(Boolean).map(s => s.text || s.message || '').join(' ');
    const text = gather(persistentSegments.filter(s => s.div_id === divId)) + ' ' + gather(transcriptSegments.filter(s => s.div_id === divId));
    return (text.trim().split(/\s+/).filter(Boolean).length) || 0;
  };

  const [isLoadingOlderSegments, setIsLoadingOlderSegments] = useState(false);
  const [canLoadMore, setCanLoadMore] = useState(false);

  const transcriptContainerRef = useRef(null);
  
  const isNewSessionRef = useRef(false);

  // Add ref for message deduplication
  const processedMessagesRef = useRef(new Set());
  // Add ref to track persistent data requests to prevent duplicates
  const persistentDataRequestedRef = useRef(false);
  const webSocketConnectedRef = useRef(false);

  // Reset persistentDataRequestedRef on mount when returning to an active session
  // This ensures transcripts are re-fetched when navigating back to record page
  useEffect(() => {
    const active = getCookie('conversation_active');
    const recordingStatus = getCookie('recording_status');
    const isLiveSessionCookie = getCookie('isLiveSession') === 'true';
    const conversationId = getCookie('conversation_id');
    
    const hasActiveSession = active === 'true' || 
                            recordingStatus === '🎙️ Listening...' || 
                            recordingStatus === 'Paused' ||
                            isLiveSessionCookie;
    
    if (hasActiveSession) {
      // Reset the flag so we can request persistent data again
      persistentDataRequestedRef.current = false;
      webSocketConnectedRef.current = false;
      
      if (persistentSegments.length === 0 && conversationId) {
      }
    }
  }, [persistentSegments.length, transcriptSegments.length]);

  // Page loading effect - Only show loading if there's an active recording session
  useEffect(() => {
    // Check if there's an active recording session
    const active = getCookie('conversation_active');
    const convId = getCookie('conversation_id');
    const recordingStatus = getCookie('recording_status');
    
    // Only show loading if there's an active live session
    const hasActiveSession = (active === 'true' && convId) || 
                            (recordingStatus === '🎙️ Listening...' || recordingStatus === 'Paused');
    
    if (hasActiveSession) {
      setIsPageLoading(true);
      
      // Show loading while restoring session data
      const loadTimer = setTimeout(() => {
        setIsPageLoading(false);
      }, 800);
      
      return () => clearTimeout(loadTimer);
    } else {
      // No active session, don't show loading
      setIsPageLoading(false);
    }
  }, []);

  // Fetch historical transcripts
  const fetchHistoricalTranscripts = async (sessionId, conversationId) => {
    const userId = getAuthenticatedUserId();
    if (!userId || !sessionId || !conversationId) {
      return [];
    }
    try {
      const response = await fetch(
        `${baseUrl}${TRANSCRIPT}?user_id=${userId}&session_id=${sessionId}&conversation_id=${conversationId}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json', 'Authorization': authIdToken } }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch historical transcripts: ${response.status}`);
      }
      const data = await response.json();
      let historicalSegments = [];
      if (data.transcription && Array.isArray(data.transcription)) {
        // Separate image segments from text segments
        const imageSegments = data.transcription.filter(item => 
          item.content_type && item.content_type.trim().toLowerCase() === 'image'
        );
        
        // Batch convert all image S3 URIs to presigned URLs
        const presignedUrlMap = await batchConvertS3UrisToPresignedUrls(
          imageSegments.map(item => ({
            div_id: item.div_id,
            full_message: item.full_message || '',
            content_type: 'image'
          })),
          authIdToken
        );

        // Process all segments
        const processedSegments = data.transcription.map((item, index) => {
          const text = item.full_message || '';
          const div_id = item.div_id || `segment-${index}`;
          const content_type = item.content_type || 'text';
          
          let finalText = text.trim();
          let type = 'text';
          
          // Handle image content type using batch-converted presigned URLs
          if (content_type.trim().toLowerCase() === 'image') {
            const presignedUrl = presignedUrlMap[div_id];
            if (presignedUrl) {
              // Create image HTML using presigned URL
              finalText = `<img src="${presignedUrl}" alt="Uploaded image" style="max-width: 300px; height: auto; border-radius: 8px; margin: 10px 0;" />`;
            } else {
              // Fallback error display
              finalText = `<div style="color: #dc3545; font-style: italic; padding: 10px; border: 1px solid #dc3545; border-radius: 8px; margin: 10px 0;">❌ Failed to load image</div>`;
            }
            type = 'image';
          }
          
          return {
            text: finalText, 
            div_id: div_id, 
            sequence_id: index, 
            final: true, 
            isPersistent: true, 
            timestamp: new Date(),
            create_timestamp: getFormattedTimestamp(new Date()), 
            shouldStartNewLine: true,
            type: type
          };
        });
        
        historicalSegments = processedSegments.filter(seg => seg.text.length > 0); // Filter out empty segments
      }
      return historicalSegments;
    } catch (error) {
      console.error('❌ Error fetching historical transcripts:', error);
      return [];
    }
  };

  // Send configuration request through websocket
  // Accepts scenario: "new", "renewal", or "refresh"
  const requestToken = (scenario = "refresh") => {
    // Don't request token if session is being stopped from TopNavbar
    if (window.isStoppingSession) {
      return false;
    }
    
    if (!socketRef.current) {
      console.warn("❌ WebSocket not available for token request");
      return false;
    }
    if (socketRef.current.readyState !== WebSocket.OPEN) {
      console.warn("❌ WebSocket not ready for token request");
      return false;
    }

    const user_cognito_id = getAuthenticatedUserId();
    if (!user_cognito_id) {
      console.error('[requestToken] User ID not available for token request');
      return false;
    }

    // hard validation, no fallback guessing
    const effectiveConversationId = conversationId || conversationIdFromUrl || getCookie('conversation_id') || localStorage.getItem('conversation_id');
    if (!effectiveConversationId || effectiveConversationId === 'null') {
      logger.error('requestToken blocked — conversation_id not ready', {
        component: COMPONENT_NAME,
        action: 'TOKEN_REQUEST_BLOCKED',
        userId: user_cognito_id,
        context: { scenario, conversationId, conversationIdFromUrl }
      });
      return false; // hard stop, don't send bad data to backend
    }

    let effectiveSessionId = sessionId || localStorage.getItem('session_id');
    if (!effectiveSessionId) {
      // Fallback: try to get from cookies
      effectiveSessionId = getCookie('session_id') || getCookie('original_session_id');
    }
     if (!effectiveSessionId) {
      logger.error('requestToken blocked — session_id not ready', {
        component: COMPONENT_NAME,
        action: 'TOKEN_REQUEST_BLOCKED',
        userId: user_cognito_id,
        context: { scenario, conversationId: effectiveConversationId }
      });
      return false; // ← hard stop
    }
    const payload = {
      action: 'configuration_speaker',
      user_cognito_id,
      session_id: effectiveSessionId,
      conversation_id: effectiveConversationId,
      persistent_data_request_type: scenario
    };


    logger.info('Sending token request via WebSocket', {
      component: COMPONENT_NAME,
      action: 'TOKEN_REQUEST',
      userId: user_cognito_id,
      context: {
        scenario,
        sessionId: effectiveSessionId,
        conversationId: conversationId || conversationIdFromUrl
      }
    });

    try {
      socketRef.current.send(JSON.stringify(payload));
      return true;
    } catch (error) {
      logger.error('Failed to send token request via WebSocket', {
        component: COMPONENT_NAME,
        action: 'TOKEN_REQUEST_FAILED',
        userId: user_cognito_id,
        context: {
          scenario,
          conversationId: conversationId || conversationIdFromUrl,
          sessionId: effectiveSessionId
        },
        error
      });
      console.error('[requestToken] ❌ Failed to send token request:', error);
      return false;
    }
  };

  // Handle persistent data from backend for reverse pagination
  const handlePersistentData = (persistentData) => {

    const dataObj = persistentData.data || persistentData;
    const records = dataObj.records || [];
    
    if (!records || records.length === 0) {
      // Even if no records, still check for session info
      if (persistentData.status) {
        restoreSessionFromBackend(persistentData.status);
      }
      // Ensure canLoadMore is false when there are no records
      setCanLoadMore(false);
      return;
    }

    // Convert backend response to your segment format (with batch S3 URI to image conversion)
    // Note: records come in reverse order (newest first), so reverse them
    const processRecordsWithImages = async () => {
      // Separate image records from text records
      const imageRecords = records.filter(record => 
        record.content_type && record.content_type.trim().toLowerCase() === 'image'
      );
      
      // Batch convert all image S3 URIs to presigned URLs
      const presignedUrlMap = await batchConvertS3UrisToPresignedUrls(
        imageRecords.map(record => ({
          div_id: record.div_id,
          full_message: record.message || '',
          content_type: 'image'
        })),
        authIdToken
      );

      const processedRecords = records
        .slice()
        .reverse()
        .map((record, index) => {
          const message = record.message || '';
          let finalText = message;
          let type = 'text';
          
          // Handle image content type using batch-converted presigned URLs
          if (record.content_type && record.content_type.trim().toLowerCase() === 'image') {
            const presignedUrl = presignedUrlMap[record.div_id];
            if (presignedUrl) {
              // Create image HTML using presigned URL
              finalText = `<img src="${presignedUrl}" alt="Uploaded image" style="max-width: 300px; height: auto; border-radius: 8px; margin: 10px 0;" />`;
            } else {
              // Fallback error display
              finalText = `<div style="color: #dc3545; font-style: italic; padding: 10px; border: 1px solid #dc3545; border-radius: 8px; margin: 10px 0;">❌ Failed to load image</div>`;
            }
            type = 'image';
          }
           else {
            // Highlight corrected words/phrases in blue if present
            const correctedWords = record.corrected_words;
            if (Array.isArray(correctedWords) && correctedWords.length > 0) {
              finalText = message;
              correctedWords.forEach(phrase => {
                if (!phrase || !phrase.trim()) return;
                // Escape special regex characters in the phrase
                const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                // Match whole phrase, case-insensitive, word boundary aware
                const regex = new RegExp(`(?<![a-zA-Z0-9-])(${escaped})(?![a-zA-Z0-9-])`, 'gi');
                finalText = finalText.replace(regex, `<span style="color:#0066cc;font-weight:500">$1</span>`);
              });
            }
          }
          
          return {
            text: finalText,
            divid: record.div_id,
            div_id: record.div_id,
            final: true,
            isPersistent: true,
            timestamp: new Date(),
            timerAtCreation: typeof record.start_time === 'number' ? record.start_time : null,
            sequenceid: index,
            shouldStartNewLine: index === 0,
            type
          };
        });
      
      return processedRecords;
    };

    // Process records with async image conversion
    processRecordsWithImages().then(historicalSegments => {
      const imageSegments = historicalSegments.filter(seg => seg.type === 'image').length;
      const mixedSegments = historicalSegments.filter(seg => seg.type === 'mixed').length;      
      if (persistentData.status) {
        restoreSessionFromBackend(persistentData.status);
      }
          
      const container = transcriptContainerRef.current;
      const prevScrollHeight = container ? container.scrollHeight : 0;
      
      setPersistentSegments(prev => {
        const existingDivIds = new Set(prev.map(seg => seg.div_id));
        const newSegments = historicalSegments.filter(seg => !existingDivIds.has(seg.div_id));
        const finalSegments = [...newSegments, ...prev];
        const finalImageSegments = finalSegments.filter(seg => seg.type === 'image').length;
        const finalMixedSegments = finalSegments.filter(seg => seg.type === 'mixed').length;
        // Prepend newSegments to prev so older messages appear at the top
        return finalSegments;
      });
      
      // Update pagination metadata if needed
      if (dataObj.total_divs !== undefined) {
        setPaginationMeta({
          totalDivs: dataObj.total_divs,
          firstPageNumber: dataObj.first_page_number,
          lastPageNumber: dataObj.last_page_number,
          currentPageNumber: dataObj.current_page_number
        });
      }
      
      // Enable loading more only if we have records AND we're not at the first page
      setCanLoadMore(records.length > 0 && dataObj.current_page_number > dataObj.first_page_number);
    
      
      // Clear loading state
      setIsLoadingOlderSegments(false);
      
      // Maintain scroll position after adding content at top
      setTimeout(() => {
        if (container && prevScrollHeight > 0) {
          const newScrollHeight = container.scrollHeight;
          const heightDifference = newScrollHeight - prevScrollHeight;
          container.scrollTop = container.scrollTop + heightDifference;
        }
      }, 50);
    }).catch(error => {
      console.error('❌ [handlePersistentData] Error processing records:', error);
      // Fallback: process without image conversion
      const fallbackSegments = records
        .slice()
        .reverse()
        .map((record, index) => ({
          text: record.message,
          divid: record.div_id,
          div_id: record.div_id,
          final: true,
          isPersistent: true,
          timestamp: new Date(),
          timerAtCreation: typeof record.start_time === 'number' ? record.start_time : null,
          sequenceid: index,
          shouldStartNewLine: index === 0
        }));
      
      setPersistentSegments(prev => {
        const existingDivIds = new Set(prev.map(seg => seg.div_id));
        const newSegments = fallbackSegments.filter(seg => !existingDivIds.has(seg.div_id));
        return [...newSegments, ...prev];
      });
    });
  };

  // On mount: restore session from cookies or localStorage if active, and restore session_id if present
  useSessionRestore({COMPONENT_NAME,conversationId,dateString,timeString,socketRef,isPausedRef,isNewSessionRef,currentDivIdRef,
  sequenceMapRef,setSessionId,setConversationId,setShareLink,setStatus,setIsPaused,setTimer,setDateString,setTimeString,setTranscriptSegments,
  setPersistentSegments,setCurrentDivId,setSequenceMap,calculateElapsedTime,getSessionStateFromCookies,clearSessionState,
  fetchHistoricalTranscripts,requestToken,saveRecordingStatus,getAuthenticatedUserId});

  // Listen for sessionId/conversationId changes to reset timer and timestamp for new session
  useEffect(() => {
  // Only reset if this is truly a NEW session (not a restoration)
  if (sessionId && conversationId) {
    const isRestoringSession = getCookie('conversation_active') === 'true' && 
                                getCookie('conversation_id') === conversationId;
    const recordingStatus = getCookie('recording_status');
    const isPausedFromCookie = getCookie('is_recording_paused') === 'true';
    const hasActiveRecording = recordingStatus === '🎙️ Listening...' || 
                               recordingStatus === 'Paused' || 
                               isPausedFromCookie;
    
    if (!isRestoringSession && !hasActiveRecording) {
      // This is a NEW session, reset everything
      setTimer(0);
      const now = new Date();
      setDateString(now.toLocaleDateString());
      setTimeString(now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase());
      
      // Only clear transcripts if this is a truly new session
      // (not when restoring from navigation)
      const conversationActive = getCookie('conversation_active');
      const isLiveSessionCookie = getCookie('isLiveSession');
      const recordingStatus = getCookie('recording_status');
      
      const isActiveSession = conversationActive === 'true' ||
                             isLiveSessionCookie === 'true' ||
                             recordingStatus === '🎙️ Listening...' ||
                             recordingStatus === 'Paused';
                             
      if (!isActiveSession) {
        setTranscriptSegments([]);
        setPersistentSegments([]);
      } 
    }
  }
}, [sessionId, conversationId]);

// Add this useEffect to handle navigation away from Record page:
useEffect(() => {
  return () => {
    const isConversationActive = getCookie('conversation_active') === 'true';
    const isRecordingPaused = getCookie('is_recording_paused') === 'true';
    const recordingStatus = getCookie('recording_status');
    const conversationId = getCookie('conversation_id');
    const isLiveSessionCookie = getCookie('isLiveSession') === 'true';
    
    // Check multiple indicators of a live session
    const isLiveSession = isConversationActive || 
                         isRecordingPaused || 
                         recordingStatus === '🎙️ Listening...' || 
                         recordingStatus === 'Paused' ||
                         isLiveSessionCookie ||
                         (conversationId && status !== 'Ready' && status !== 'Stopped');

    // ❗ Only clear if session is truly stopped
    if (!isLiveSession) {
      setTranscriptSegments([]);
      setPersistentSegments([]);
      setCurrentDivId(null);
      currentDivIdRef.current = null;
      setSequenceMap({});
      sequenceMapRef.current = {};
    } 
  };
}, [status]);

  // Keep timestamp updated every minute when there's no live session
  useEffect(() => {
    // Check both isLive state and cookies for active session
    const isConversationActive = getCookie('conversation_active') === 'true';
    const isRecordingPaused = getCookie('is_recording_paused') === 'true';
    const hasActiveSession = isLive || isConversationActive || isRecordingPaused;
    
    if (!hasActiveSession) {
      const updateTimestamp = () => {
        const now = new Date();
        setDateString(now.toLocaleDateString());
        setTimeString(now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase());
      };
      updateTimestamp();
      
      // Calculate ms until the next minute starts
      const now = new Date();
      const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();
      
      // First update at the next minute, then every 60 seconds
      const timeoutId = setTimeout(() => {
        updateTimestamp();
        const intervalId = setInterval(updateTimestamp, 60000);
        // Store intervalId for cleanup
        window._timestampIntervalId = intervalId;
      }, msUntilNextMinute);
      
      return () => {
        clearTimeout(timeoutId);
        if (window._timestampIntervalId) {
          clearInterval(window._timestampIntervalId);
          window._timestampIntervalId = null;
        }
      };
    }
  }, [isLive, setDateString, setTimeString]);

  // Timer for recording
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused, isPausedRef]);

  // Keep statusRef in sync with status
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    const tick = () => {
      if (!isPausedRef.current && !isReconnectingRef.current) {
        const elapsed = calculateElapsedTime();

        //  Update ONLY when second actually changes
        if (elapsed !== lastRenderedSecondRef.current) {
          lastRenderedSecondRef.current = elapsed;
          setTimer(elapsed);
          
          // Update timer display and store as last known value
          lastKnownTimerRef.current = elapsed;

          if (sessionId) {
            saveTimerToStorage(sessionId, elapsed);
          }
        }
      }

      rafIdRef.current = requestAnimationFrame(tick);
    };

    rafIdRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [sessionId]);

  // Expose mediaRecorder and audioChunks globally for TopNavbar access
 useEffect(() => {
  window.mediaRecorderRef = {
    current: mediaRecorderRef.current,
    audioChunks: audioChunksRef.current
  };
  
  // Update when mediaRecorder changes
  return () => {
    window.mediaRecorderRef = null;
  };
}, [mediaRecorderRef.current, audioChunksRef.current]);

// Also expose other refs globally - run once on mount
 useEffect(() => {
  window.assemblySocketRef = assemblySocketRef;
  window.workletNodeRef = workletNodeRef;
  window.socketRef = socketRef;
  window.audioContextRef = audioContextRef;
  window.streamRef = streamRef;
  window.mediaRecorderRef = mediaRecorderRef;
  
  return () => {
    window.assemblySocketRef = null;
    window.workletNodeRef = null;
    window.audioContextRef = null;
    window.streamRef = null;
    window.mediaRecorderRef = null;
  };
}, []);

  // Token expiry management functions
  const startTokenExpiryTimer = () => {
    // Clear any existing timer first
    if (tokenCheckTimer) {
      clearInterval(tokenCheckTimer);
    }
    
    const timer = setInterval(() => {
      checkTokenExpiry();
    }, configDetails.tokenCheckIntervalMinutes * 60 * 1000); // Check interval from config
    
    setTokenCheckTimer(timer);
  };

  const stopTokenExpiryTimer = () => {
    if (tokenCheckTimer) {
      clearInterval(tokenCheckTimer);
      setTokenCheckTimer(null);
    }
  };

  const checkTokenExpiry = () => {
    // Don't check if user initiated stop or session is stopping
    if (userInitiatedStopRef.current || window.isStoppingSession) {
      return;
    }

    const expiresAt = getCookie('token_expires_at');
    if (!expiresAt) {
       logger.debug('Token expiry check skipped - no expiration date found', {
        component: COMPONENT_NAME,
        action: 'TOKEN_EXPIRY_CHECK_SKIPPED',
        userId: getAuthenticatedUserId(),
        context: {
          reason: 'no_expires_at_cookie',
          conversationId: conversationId || getCookie('conversation_id'),
          sessionId
        }
      });
      return;
    }
    // Normalize expiresAt to UTC (append 'Z' if missing timezone info)
    let expiresAtUTC = expiresAt;
    if (!expiresAt.endsWith('Z') && !expiresAt.includes('+') && !expiresAt.includes('-', 10)) {
      expiresAtUTC = expiresAt + 'Z';
    }
    
    const now = new Date();
    const expiry = new Date(expiresAtUTC);
    if (isNaN(expiry.getTime())) {
      logger.error('Invalid token expiration date during expiry check', {
        component: COMPONENT_NAME,
        action: 'TOKEN_EXPIRY_CHECK_INVALID_DATE',
        userId: getAuthenticatedUserId(),
        context: {
          rawExpiresAt: expiresAt,
          parsedAsUTC: expiresAtUTC,
          conversationId: conversationId || getCookie('conversation_id'),
          sessionId
        }
      });
      console.error('❌ [checkTokenExpiry] Invalid expiration date:', expiresAt);
      return;
    }
    const timeDiffMinutes = (expiry - now) / (1000 * 60); // Difference in minutes
    
    
    if (timeDiffMinutes < configDetails.tokenRenewalThresholdMinutes) {
      logger.info('Token nearing expiry - requesting renewal', {
        component: COMPONENT_NAME,
        action: 'TOKEN_RENEWAL_TRIGGERED',
        userId: getAuthenticatedUserId(),
        context: {
          minutesUntilExpiry: timeDiffMinutes.toFixed(2),
          renewalThresholdMinutes: configDetails.tokenRenewalThresholdMinutes,
          expiresAt: expiry.toISOString(),
          conversationId: conversationId,
          sessionId: sessionId,
          transcriptionModel: getStoredModel() || null
        }
      });
      requestTokenRenewal();
    }
  };

  const requestTokenRenewal = () => {
    // Don't request token if session is being stopped from TopNavbar
    if (window.isStoppingSession) {
      return;
    }

        // NFGI-908: Don't request token renewal if within 3 minutes of per-conversation time limit expiry
    // This prevents repeated token renewal requests when the conversation is about to be terminated
    if (isNearTimeLimitExpiry(timeLimitExpiresAt)) {
      logger.debug('Token renewal skipped - near per-conversation time limit', {
        component: COMPONENT_NAME,
        action: 'TOKEN_RENEWAL_SKIPPED_TIME_LIMIT',
        userId: getAuthenticatedUserId(),
        context: {
          minutesUntilExpiry: ((new Date(timeLimitExpiresAt).getTime() - Date.now()) / (1000 * 60)).toFixed(2),
          timeLimitExpiresAt,
          conversationId: conversationId || getCookie('conversation_id'),
          sessionId: sessionId || getCookie('session_id')
        }
      });
      return;
    }

    // Only call requestToken if socket is available and open
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
  requestToken("renewal");
    }
  };

  // Reconnection state management
  const reconnectionAttemptsRef = useRef(0);
  const lastReconnectionTimeRef = useRef(0);
  const reconnectionTimeoutRef = useRef(null);
  const isStartingRef = useRef(false);
  // Shared deps for forceAssemblyReconnect (used by pause-resume recovery and the transcription watchdog)
  const buildForceReconnectDeps = () => ({reconnectionLockRef,reconnectionTimeoutRef,reconnectionAttemptsRef,isReconnectingRef,lastKnownTimerRef,
    reconnectStartedAtRef,assemblySocketRef,workletNodeRef,audioContextRef,streamRef,conversationId,sessionId,timer,s3AudioRecorder,
    setIsReconnecting,setS3AudioRecorder,setS3AudioChunks,getAuthenticatedUserId,getStoredModel,getStoredToken,requestTokenRenewal,
    startTranscriptionStreamWithToken
  });
  const handleAssemblyDisconnectionWrapper =() =>handleAssemblyDisconnection({
      userInitiatedStopRef,statusRef,isReconnectingRef,lastReconnectionTimeRef,reconnectionAttemptsRef,lastKnownTimerRef,
      reconnectStartedAtRef,reconnectionTimeoutRef,conversationId,sessionId,timer,setIsReconnecting,setStatus,getAuthenticatedUserId,
      getStoredModel,getStoredToken,requestTokenRenewal,startTranscriptionStreamWithToken,configDetails
    });
  useEffect(() => {
    if (transcriptContainerRef.current) {
      transcriptContainerRef.current.scrollTop = transcriptContainerRef.current.scrollHeight;
    }
  }, [transcriptSegments]);

  // Scroll detection for reverse pagination
  useEffect(() => {
    const container = transcriptContainerRef.current;
    if (!container) return;
    
    const handleScroll = () => {
      // Check if scrolled to top and can load more
      if (container.scrollTop === 0 && canLoadMore && !isLoadingOlderSegments) {
        loadOlderSegments();
      }
    };
    
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [canLoadMore, isLoadingOlderSegments]);

  // Track user login state changes
  useEffect(() => {
    const interval = setInterval(() => {
      const currentUserId = getAuthenticatedUserId();
      if (currentUserId !== userId) {
        setUserId(currentUserId);
      }
    }, 1000); // Check every second instead of every render
    
    return () => clearInterval(interval);
  }, [userId]);

  // On mount: redirect if not logged in
  useEffect(() => {
    const userId = getAuthenticatedUserId();
    
    // ✅ Extract conversation_id from URL
    const pathSegments = window.location.pathname.split('/');
    const urlConvId = pathSegments[pathSegments.length - 1];
    const hasConversationInUrl = urlConvId && urlConvId !== 'record' && urlConvId.length > 10;
    
    if (!userId) {
      logger.info('User not authenticated - redirecting to login', {
        component: COMPONENT_NAME,
        action: 'AUTH_REDIRECT',
        context: {
          hasConversationInUrl,
          conversationIdFromUrl: hasConversationInUrl ? urlConvId : null,
          currentPath: window.location.pathname,
          redirectPath: hasConversationInUrl ? window.location.pathname : null
        }
      });
      if (hasConversationInUrl) {
        // ✅ Store the intended destination before redirecting to login
        const redirectPath = window.location.pathname;
        localStorage.setItem('redirect_after_login', redirectPath);
        // Additional verification
        const verifyStorage = localStorage.getItem('redirect_after_login');
      }
      
      navigate(routesfe.LOGIN);
    }
  }, [navigate]);

  // Effect 1: Extract conversation_id from URL on mount
  useEffect(() => {
    const pathSegments = window.location.pathname.split('/');
    const urlConversationId = pathSegments[pathSegments.length - 1];
    
    if (urlConversationId && urlConversationId !== 'record' && urlConversationId.length > 10) {
      if (!isNewSessionRef.current) {
        isNewSessionRef.current = false;
      }
      
      setConversationIdFromUrl(urlConversationId);
      setConversationId(urlConversationId);
      setCookie('conversation_id', urlConversationId);
      setShareLink(`${window.location.origin}/watch/${urlConversationId}`);
      const validToken = getStoredToken();
      if (!validToken) {
        // Token expired - clear it to prevent connection attempts
        deleteCookie('transcription_token');
        deleteCookie('token_issued_at');
        deleteCookie('token_expires_at');
        deleteCookie('token_status');
      }
    }
  }, []); // Runs once to extract URL

  const reconnectWebSocketLocal = () => {
    
    // Restore session data from localStorage if missing
    let currentConversationId = conversationId;
    let currentSessionId = sessionId;
    
    if (!currentConversationId) {
      currentConversationId = localStorage.getItem('conversation_id') || conversationIdFromUrl;
    }
    
    if (!currentSessionId) {
      currentSessionId = localStorage.getItem('session_id');
    }
        
    reconnectWebSocket({
      getAuthenticatedUserId,
      conversationId: currentConversationId,
      conversationIdFromUrl,
      sessionId: currentSessionId,
      socketRef,
      webSocketConnectedRef,
      wsConnectLatchRef,
      persistentDataRequestedRef,
      websocketBaseUrl,
      requestToken,
      handlePersistentData,
      startTranscriptionStreamWithToken,
      startTokenExpiryTimer,
      getSessionIdForConversation,
      setSessionId,
      setDateString,
      setTimeString,
      assemblySocketRef,
      isLive,
      userInitiatedStopRef,
      showPlanWarningToast,
      schedulePlanCountdownFromPayload
    });
  };

  // ===== WEBSOCKET CONNECTION HOOK =====
  // The main WebSocket connection logic has been extracted to useWebSocketConnection hook
  // This includes: connection setup, onopen, onmessage, onclose, onerror handlers, and cleanup
  // See: src/hooks/useWebSocketConnection.js

  // Check for new session or resume ongoing session flag and auto-start/restore
  useEffect(() => {
    const shouldStartNew = sessionStorage.getItem('start_new_session');
    const shouldResumeOngoing = sessionStorage.getItem('resume_ongoing_session');
    
    if (shouldStartNew === 'true') {
      // Mark this as a new session
      isNewSessionRef.current = true;
      sessionStorage.setItem('session_creation_flag', 'true');
      
       // Force close any lingering websocket connections
      if (window.assemblySocketRef?.current) {
        try {
          window.assemblySocketRef.current.onclose = null;
          window.assemblySocketRef.current.onerror = null;
          window.assemblySocketRef.current.onopen = null;
          window.assemblySocketRef.current.onmessage = null;
          window.assemblySocketRef.current.close();
        } catch (e) {}
        window.assemblySocketRef.current = null;
      }
      if (window.socketRef?.current) {
        try {
          window.socketRef.current.onclose = null;
          window.socketRef.current.onerror = null;
          window.socketRef.current.onopen = null;
          window.socketRef.current.onmessage = null;
          window.socketRef.current.close();
        } catch (e) {}
        window.socketRef.current = null;
      }
      if (typeof webSocketConnectedRef !== 'undefined') {
        webSocketConnectedRef.current = false;
      }
      // Explicitly reset all session state before starting new session
      setTranscriptSegments([]);
      setTimer(0);
      setIsPaused(false);
      setSessionId(null);
      setConversationId(null);
      setPersistentSegments([]);
      if (typeof resetForNewSession === 'function') {
        resetForNewSession();
      }
      sessionStorage.removeItem('start_new_session');
      setTimeout(() => {
        handleStart();
      }, 100);
    } else if (shouldResumeOngoing === 'true') {
      // Mark this as session restoration, not new
      isNewSessionRef.current = false;
      
      sessionStorage.removeItem('resume_ongoing_session');
      // Restore transcript and timer from cookies/localStorage
      const transcript = localStorage.getItem('transcript_segments');
      const timerValue = getCookie('recording_start_time');
      const totalPause = getCookie('total_pause_duration');
      const isPaused = getCookie('is_recording_paused') === 'true';
      if (transcript) {
        try {
          setTranscriptSegments(JSON.parse(transcript));
        } catch (e) {
          console.warn('[Record] Failed to parse transcript_segments from localStorage');
        }
      }
      if (timerValue) {
        // Calculate elapsed time
        let elapsed = Math.floor((Date.now() - parseInt(timerValue, 10)) / 1000);
        if (totalPause) {
          elapsed -= parseInt(totalPause, 10);
        }
        setTimer(Math.max(0, elapsed));
      }
      setIsPaused(isPaused);
    }
  }, []); 
  useEffect(() => {
    if (transcriptSegments && transcriptSegments.length > 0) {
      localStorage.setItem('transcript_segments', JSON.stringify(transcriptSegments));
    }
  }, [transcriptSegments]);

  // Token expiry timer lifecycle management
  useEffect(() => {
    // Start timer if we have a valid token
    const storedToken = getStoredToken();
    if (storedToken && isLive) {
      startTokenExpiryTimer();
    }
    
    // Cleanup timer on unmount or when recording stops
    return () => {
      stopTokenExpiryTimer();
    };
  }, [isLive]);


  useEffect(() => {
    if (!sessionId) {
      return;
    }
    // If already listening, do NOT start again
    if (isLive) {
      return;
    }
    startAssemblyStream();
  }, [sessionId]);

  // Waveform
  useEffect(() => {
    if (status === '🎙️ Listening...' && !isPaused && analyserRef.current && waveformRef.current) {
      const canvas = waveformRef.current;
      const ctx = canvas.getContext('2d');
      const analyser = analyserRef.current;
      const bufferLength = analyser.fftSize;
      const dataArray = new Uint8Array(bufferLength);

      function draw() {
        if (isPaused) {
          return;
        }
        analyser.getByteTimeDomainData(dataArray);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.beginPath();
        for (let i = 0; i < bufferLength; i++) {
          const x = (i / bufferLength) * canvas.width;
          const y = (dataArray[i] / 255) * canvas.height;
          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.strokeStyle = isPaused ? '#6c757d' : '#1a73e8';
        ctx.lineWidth = 2;
        ctx.stroke();
        if (!isPaused) {
          animationFrameRef.current = requestAnimationFrame(draw);
        }
      }
      draw();
      return () => {
        cancelAnimationFrame(animationFrameRef.current);
      };
    } else if (waveformRef.current) {
      const canvas = waveformRef.current;
      const ctx = canvas.getContext('2d');
      const width = canvas.width, height = canvas.height;
      ctx.clearRect(0, 0, width, height);
      ctx.beginPath();
      ctx.moveTo(0, height / 2);
      ctx.lineTo(width, height / 2);
      ctx.strokeStyle = '#1a73e8';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }, [status, isPaused, analyserRef, waveformRef, animationFrameRef]);



  // Helper to get new div_id (must be inside Record to access sessionId)
  const getNewDivId = () => {
    // Ensure sessionId is always available - check state first, then localStorage/cookies as fallback
    const effectiveSessionId = sessionId || localStorage.getItem('session_id') || getCookie('originalsessionid') || getCookie('sessionid') || 'nosession';
    return `${effectiveSessionId}-${generateRandomAlpha()}`;
  };

  // Helpers
  const generateNewSessionId = () => {
    const userId = getAuthenticatedUserId();
    const sessionId = userId ? `${userId}-${Date.now()}` : null;
    return sessionId;
  };

  const sendMessageToBackend = (payload, onSuccess) => {
    const user_id = getAuthenticatedUserId();
    if (!user_id || !payload || typeof payload !== 'object') {
      logger.warn('sendMessageToBackend called with invalid payload', {
        component: COMPONENT_NAME,
        action: 'SEND_MESSAGE_INVALID_PAYLOAD',
        userId: user_id,
        context: {
          hasUserId: !!user_id,
          hasPayload: !!payload,
          payloadType: typeof payload,
          conversationId: conversationId || getCookie('conversation_id'),
          sessionId
        }
      });
      console.error('[sendMessageToBackend] Invalid payload:', payload);
      return false;
    }
    const autoDeleteEnabled = isAutoDeleteEnabled();
    if (autoDeleteEnabled) {
      logger.debug('sendMessageToBackend skipped - auto-delete enabled', {
        component: COMPONENT_NAME,
        action: 'SEND_MESSAGE_SKIPPED',
        userId: user_id,
        context: {
          reason: 'auto_delete_enabled',
          conversationId: conversationId || getCookie('conversation_id'),
          sessionId
        }
      });
      return false;
    }
    if (!payload.div_id || typeof payload.sequence_id !== 'number' || !payload.text || !payload.create_timestamp) {
      logger.warn('sendMessageToBackend called with missing required fields', {
        component: COMPONENT_NAME,
        action: 'SEND_MESSAGE_MISSING_FIELDS',
        userId: user_id,
        context: {
          hasDivId: !!payload.div_id,
          hasSequenceId: typeof payload.sequence_id === 'number',
          hasText: !!payload.text,
          hasTimestamp: !!payload.create_timestamp,
          conversationId: conversationId || getCookie('conversation_id'),
          sessionId
        }
      });
      console.error('[sendMessageToBackend] Missing required fields:', payload);
      return false;
    }

    const lastSentData = lastSentTextRef.current[payload.div_id];
    const currentTime = new Date(payload.create_timestamp);

    if (lastSentData) {
      const lastTime = new Date(lastSentData.timestamp);
      const timeDifference = (currentTime - lastTime) / 1000;
      if (lastSentData.text === payload.text.trim() && timeDifference < 1) {
        return false;
      }
    }

    lastSentTextRef.current[payload.div_id] = {
      text: payload.text.trim(),
      timestamp: payload.create_timestamp
    };

    const conversationIdFromCookies = getCookie('conversation_id') || localStorage.getItem('conversation_id');

    let effectiveSessionId = sessionId;
    
    // If sessionId is null/empty (incognito mode), try to get from localStorage or use conversation_id
    if (!effectiveSessionId) {
      const storedSessionId = localStorage.getItem('session_id');
      if (storedSessionId) {
        effectiveSessionId = storedSessionId;
      } else {
        // Fallback to conversation_id (this should work if backend handles it)
        effectiveSessionId = conversationId || conversationIdFromUrl;
      }
    }

    // Calculate duration in seconds for this message
    let duration = 0;
    if (typeof calculateElapsedTime === 'function') {
      duration = calculateElapsedTime();
    }
    
    // Ensure proper spacing: add leading space for continuation segments (sequence_id > 0)
    // This ensures that when backend concatenates segments, words are properly separated
    let textToSend = payload.text.trim();
    if (payload.sequence_id > 0 && textToSend.length > 0) {
      textToSend = ' ' + textToSend;
    }
     //Ensure proper conversation_id fallback (never null)
    const effectiveConversationId = conversationId || conversationIdFromCookies || conversationIdFromUrl || getCookie('conversation_id');
    fetch(`${baseUrl}${INSERT_MESSAGE}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': authIdToken },
      body: JSON.stringify({
        user_id,
        session_id: effectiveSessionId,
        conversation_id: effectiveConversationId,
        text: textToSend,
        div_id: payload.div_id,
        sequence_id: payload.sequence_id,
        create_timestamp: payload.create_timestamp,
        duration, // Insert duration in seconds
        words: payload.words ?? [] // Word-level timings from Deepgram
      })
    })
    .then(response => response.json())
    .then(data => {
      logger.debug('Message sent to backend successfully', {
        component: COMPONENT_NAME,
        action: 'SEND_MESSAGE_SUCCESS',
        userId: user_id,
        context: {
          divId: payload.div_id,
          sequenceId: payload.sequence_id,
          textLength: textToSend.length,
          conversationId: conversationId || conversationIdFromCookies,
          sessionId: effectiveSessionId,
          duration,
          response: data // Print the entire response
        }
      });
      if (onSuccess) onSuccess();
    })
    .catch(error => {
      logger.error('Failed to send message to backend', {
        component: COMPONENT_NAME,
        action: 'SEND_MESSAGE_FAILED',
        userId: user_id,
        context: {
          divId: payload.div_id,
          sequenceId: payload.sequence_id,
          conversationId: conversationId || conversationIdFromCookies,
          sessionId: effectiveSessionId
        },
        error
      });
      console.error('❌ [sendMessageToBackend] Error:', error);
      delete lastSentTextRef.current[payload.div_id];
    });

    return true;
  };


  const getSessionIdForConversation = async (conversationId) => {
    try {
      // First check if session_id is stored in localStorage (normal mode)
      const storedSessionId = localStorage.getItem("session_id");
      if (storedSessionId) {
        return storedSessionId;
      }
      
      // In incognito mode, check if we can find session_id via user query
      const userId = getAuthenticatedUserId();
      if (!userId) {
        console.warn('⚠️ [Session ID] No user ID available');
        return null;
      }
      
      const response = await fetch(`${baseUrl}${META_DATA}?user_id=${userId}&page=1&limit=10`,
        { method: 'GET', headers: { 'Content-Type': 'application/json', 'Authorization': authIdToken } }
      );

      if (!response.ok) {
        console.error('❌ [Session ID] Meta data request failed:', response.status);
        return null;
      }

      const data = await response.json();

      // Find the record with matching conversation_id
      const matchingRecord = data.records?.find(record => record.conversation_id === conversationId);
      
      if (matchingRecord) {
        return matchingRecord.session_id;
      } else {
        console.warn('⚠️ [Session ID] No matching conversation_id found in meta_data');
        return null;
      }
      
    } catch (error) {
      console.error('❌ [Session ID] Error getting session_id:', error);
      return null;
    }
  };

  const restoreSessionFromBackend = (statusInfo) => {
    
    try {
      // Extract session information from status object
      const { current_status, time } = statusInfo;

      // Restore recording status based on backend status
      if (current_status) {
        
        // Map backend status to frontend status
        let frontendStatus = 'Ready';
        let isPausedState = false;
        
        switch (current_status) {
          case 'LIVE':
            frontendStatus = '🎙️ Listening...';
            isPausedState = false;
            break;
          case 'PAUSED':
            frontendStatus = 'Paused';
            isPausedState = true;
            break;
          case 'STOPPED':
            // Clear live session cookies
            clearLiveSessionCookies();
            
            // Clear all session-related cookies
            const sessionCookiesToClear = [
              'recording_status',
              'session_id',
              'recording_start_time',
              'is_recording_paused',
              'pause_start_time',
              'total_pause_duration',
              'last_activity_time',
              'original_session_id',
              'conversation_active',
              'transcription_token',
              'token_issued_at',
              'token_expires_at',
              'token_status'
            ];
            sessionCookiesToClear.forEach(cookieName => deleteCookie(cookieName));
            
            // Clear localStorage items
            localStorage.removeItem('session_id');
            localStorage.removeItem('transcript_segments');
            if (sessionId) {
              localStorage.removeItem(`timer_${sessionId}`);
            }
            if (conversationId) {
              localStorage.removeItem(`timer_${conversationId}`);
            }
            
            // Stop any active reconnection attempts
            if (reconnectionTimeoutRef.current) {
              clearTimeout(reconnectionTimeoutRef.current);
              reconnectionTimeoutRef.current = null;
            }
            reconnectionAttemptsRef.current = configDetails.MAX_RECONNECTION_ATTEMPTS;
            isReconnectingRef.current = false;
            setIsReconnecting(false);
            
            // Stop token expiry timer
            stopTokenExpiryTimer();
            
            // Close transcription socket if open
            if (assemblySocketRef.current) {
              try {
                assemblySocketRef.current.onclose = null;
                assemblySocketRef.current.onerror = null;
                assemblySocketRef.current.onopen = null;
                assemblySocketRef.current.onmessage = null;
                if (assemblySocketRef.current.readyState === WebSocket.OPEN || 
                    assemblySocketRef.current.readyState === WebSocket.CONNECTING) {
                  assemblySocketRef.current.close();
                }
              } catch (e) {
                console.warn('[Session Restore STOPPED] Error closing transcription socket:', e);
              }
              assemblySocketRef.current = null;
            }
            
            // Reset UI state
            setStatus('Ready');
            setTimer(0);
            setIsPaused(false);
            isPausedRef.current = false;
            setTranscriptSegments([]);
            setPersistentSegments([]);
            setDateString('');
            setTimeString('');
            
            // Reset context
            if (typeof resetAfterSessionCompleted === 'function') {
              resetAfterSessionCompleted();
            }
            
            // Navigate to home page
            navigate(routesfe.HOME);
            return; // Exit early since we're navigating away
          default:
            frontendStatus = 'Ready';
            isPausedState = false;
        }
        
        setStatus(frontendStatus);
        
        setIsPaused(isPausedState);
        isPausedRef.current = isPausedState;
      }

      // Restore timer from backend time
      if (typeof time === 'number') {
        setTimer(time);
      }

      // Set date/time if needed
      if (!dateString || !timeString) {
        const now = new Date();
        setDateString(now.toLocaleDateString());
        setTimeString(now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase());
      }

      // Generate a session ID for this restored session (needed for UI functionality)
      if (!sessionId) {
        const restoredSessionId = `${getAuthenticatedUserId()}-${Date.now()}`;
        setSessionId(restoredSessionId);
      }

    } catch (error) {
      console.error('❌ [Session Restore] Error restoring session state:', error);
    }
  };

  // Load older segments when user scrolls to top
  const loadOlderSegments = () => {
    if (!canLoadMore || isLoadingOlderSegments) {
      return;
    }
    setIsLoadingOlderSegments(true);
    // Get session_id from state or cookies
    let effectiveSessionId = sessionId;
    if (!effectiveSessionId) {
      effectiveSessionId = getCookie('session_id') || getCookie('sessionid');
    }
    //Ensure proper conversation_id fallback (never null)
    const effectiveConversationId = conversationId || conversationIdFromUrl || getCookie('conversation_id');
    const requestPayload = {
      action: "persistent_data",
      user_cognito_id: getAuthenticatedUserId(),
      session_id: effectiveSessionId,
      conversation_id: effectiveConversationId,
      conversation_id: effectiveConversationId,
      total_divs: paginationMeta.totalDivs,
      first_page_number: paginationMeta.firstPageNumber,
      last_page_number: paginationMeta.lastPageNumber,
      current_page_number: paginationMeta.currentPageNumber - 1
    };
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(requestPayload));
    } else {
      setIsLoadingOlderSegments(false);
    }
  };
     const handleAddManualText = (text) => {
      const userId = getAuthenticatedUserId();
    logger.debug('Manual text addition initiated', {
      component: COMPONENT_NAME,
      action: 'ADD_MANUAL_TEXT',
      userId,
      context: {
        textLength: text?.length || 0,
        isPaused: isPausedRef.current,
        status: statusRef.current
      }
    });
    if (isPausedRef.current || statusRef.current === 'Paused' || statusRef.current === 'Stopped') {
      logger.debug('Manual text blocked - session not active', {
        component: COMPONENT_NAME,
        action: 'MANUAL_TEXT_BLOCKED',
        userId,
        context: { reason: 'session_paused_or_stopped' }
      });
      return;
    }
    if (!text || !text.trim() || !socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      return;
    }
    // Get conversation_id from state, URL, or cookies (never null)
    let effectiveConversationId = conversationId || conversationIdFromUrl || getCookie('conversation_id');
    if (!effectiveConversationId || effectiveConversationId === 'null') {
      console.warn('[handleAddManualText] No valid conversation_id, manual segment not sent');
      return;
    }

    // Generate a new segment with a unique div_id for manual text
    const segment = createSegment(text,transcriptSegments,currentDivId,setCurrentDivId,sequenceMap,setSequenceMap,true,
    {sessionId,getNewDivId,sequenceMapRef,currentDivIdRef,lastParagraphTimestampRef,lastParagraphDivIdRef,getFormattedTimestamp,generateRandomAlpha,calculateElapsedTime});
    // Send to viewers
    const payload = {
      action: 'segment',
      conversation_id: effectiveConversationId,
      text: text.trim(),
      is_partial: false,
      is_pause: isPausedRef.current,
      action_type: 'manual_text',
      source: 'manual',
      div_id: segment.div_id,
      start_time: calculateElapsedTime()
    };
    socketRef.current.send(JSON.stringify(payload));
    sendMessageToBackend(segment);
    setTranscriptSegments(prev => [...prev, segment]);
  };

  const handleTranscriptMessage = (res) => {

    if (!res || !res.text?.trim()) return;
        if ((res.message_type === "PartialTranscript" || res.message_type === "FinalTranscript") && res.text?.trim() && !isPausedRef.current) {
          lastTranscriptTimeRef.current = Date.now();
        }

        if (res.message_type === "PartialTranscript" && res.text?.trim() && !isPausedRef.current) {
          processedMessagesRef.current.add(res.text + res.message_type);
          if (processedMessagesRef.current.size > 50) {
            const entries = Array.from(processedMessagesRef.current);
            processedMessagesRef.current = new Set(entries.slice(-25));
          }
          const currentTime = new Date();
          let newDivIdForState = null;
          let newSequenceMapUpdate = null;
          setTranscriptSegments(prev => {
            let div_id = currentDivIdRef.current;
            let sequence_id = 0;
            let startNewParagraph = false;
            if (prev.length > 0) {
              const lastSegment = prev[prev.length - 1];
              const timeDifference = (currentTime - lastSegment.timestamp) / 1000;
              // Start new paragraph if enough time passed OR if the last segment was an image OR if div reached max words
              if (timeDifference >= PARAGRAPH_BREAK_SECONDS || lastSegment.type === 'image' || countWordsInDiv(currentDivIdRef.current) >= MAX_WORDS_PER_DIV) {
                startNewParagraph = true;
              }
            } else {
              startNewParagraph = true;
            }
            // Only generate a new div_id if this is a true new paragraph (not already handled)
            if (startNewParagraph) {
              // If lastParagraphTimestampRef is recent, reuse lastParagraphDivIdRef
              if (
                lastParagraphTimestampRef.current &&
                Math.abs(currentTime - lastParagraphTimestampRef.current) < 1000 &&
                lastParagraphDivIdRef.current
              ) {
                div_id = lastParagraphDivIdRef.current;
                sequence_id = 0;
                // No need to update refs/state
              } else {
                div_id = getNewDivId();
                currentDivIdRef.current = div_id;
                setCurrentDivId(div_id);
                sequence_id = 0;
                sequenceMapRef.current[div_id] = 0;
                newSequenceMapUpdate = { [div_id]: 0 };
                lastParagraphTimestampRef.current = currentTime;
                lastParagraphDivIdRef.current = div_id;
                paragraphStartTimerRef.current = calculateElapsedTime();
              }
            } else {
              div_id = prev.length > 0 ? (prev[prev.length - 1].div_id || currentDivIdRef.current || getNewDivId()) : getNewDivId();
              // For PartialTranscript: use the CURRENT sequence_id from sequenceMapRef 
              sequence_id = sequenceMapRef.current[div_id] || 0;
            }
            sendSegmentToViewers(res.text,true,null,div_id,sequence_id,getFormattedTimestamp(currentTime),
            {
                socketRef,
                isPausedRef,
                conversationId,
                conversationIdFromUrl,
                previousConversationId
              },
              false,
              paragraphStartTimerRef.current
            );
            return [...prev.filter(seg => seg.final), {
              text: res.text,
              final: false,
              timestamp: currentTime,
              shouldStartNewLine: startNewParagraph,
              div_id,
              sequence_id,
              create_timestamp: getFormattedTimestamp(currentTime),
              timerAtCreation: paragraphStartTimerRef.current,
            }];
          });
          if (newSequenceMapUpdate) {
            setSequenceMap(map => ({ ...map, ...newSequenceMapUpdate }));
          }
        }

        if (res.message_type === "FinalTranscript" && res.text?.trim() && !isPausedRef.current) {
          // CRITICAL: Check for duplicates before adding
          const messageKey = res.text + res.message_type;
          if (processedMessagesRef.current.has(messageKey)) {
            return;
          }
          processedMessagesRef.current.add(res.text + res.message_type);
          if (processedMessagesRef.current.size > 50) {
            const entries = Array.from(processedMessagesRef.current);
            processedMessagesRef.current = new Set(entries.slice(-25));
          }
          const currentTime = new Date();
          let newDivIdForState = null;
          let newSequenceMapUpdate = null;
          setTranscriptSegments(prev => {
            let div_id = currentDivIdRef.current;
            let sequence_id = 0;
            let startNewParagraph = false;
            if (prev.length > 0) {
              const lastSegment = prev[prev.length - 1];
              const timeDifference = (currentTime - lastSegment.timestamp) / 1000;
              // Start new paragraph if enough time passed OR if the last segment was an image OR if div reached max words
              if (timeDifference >= PARAGRAPH_BREAK_SECONDS || lastSegment.type === 'image' || countWordsInDiv(currentDivIdRef.current) >= MAX_WORDS_PER_DIV) {
                startNewParagraph = true;
              }
            } else {
              startNewParagraph = true;
            }
            if (startNewParagraph) {
              if (
                lastParagraphTimestampRef.current &&
                Math.abs(currentTime - lastParagraphTimestampRef.current) < 1000 &&
                lastParagraphDivIdRef.current
              ) {
                div_id = lastParagraphDivIdRef.current;
                sequence_id = 0;
              } else {
                div_id = getNewDivId();
                newDivIdForState = div_id;
                sequence_id = 0;
                sequenceMapRef.current[div_id] = 0;
                newSequenceMapUpdate = { [div_id]: 0 };
                lastParagraphTimestampRef.current = currentTime;
                lastParagraphDivIdRef.current = div_id;
                paragraphStartTimerRef.current = calculateElapsedTime();
              }
            } else {
              div_id = prev.length > 0 ? (prev[prev.length - 1].div_id || currentDivIdRef.current || getNewDivId()) : getNewDivId();
              // For FinalTranscript: use the CURRENT sequence_id from sequenceMapRef (should match PartialTranscript)
              sequence_id = sequenceMapRef.current[div_id] || 0;
            }
            const segment = {
              text: res.text,
              div_id,
              sequence_id,
              create_timestamp: getFormattedTimestamp(currentTime),
              timestamp: currentTime,
              shouldStartNewLine: startNewParagraph,
              final: true,
              timerAtCreation: paragraphStartTimerRef.current,
            };
            
            const deepgramWords = (res?.words && res.words.length > 0)
              ? res.words
              : (res?.channel?.alternatives?.[0]?.words ?? []);

            let absoluteWords = deepgramWords;
            if (deepgramWords.length > 0) {
              const lastWordEnd = deepgramWords[deepgramWords.length - 1].end ?? 0;
              const cookieOffset = chunkAudioOffsetRef.current ?? 0;
              let chunkStartOffset;
              if (cookieOffset > 0) {
                chunkStartOffset = cookieOffset;
              } else {
                const elapsedS = typeof calculateElapsedTime === 'function'
                  ? calculateElapsedTime()
                  : 0;
                chunkStartOffset = Math.max(0, elapsedS - lastWordEnd);
              }

              absoluteWords = deepgramWords.map(w => ({
                ...w,
                start: parseFloat((w.start + chunkStartOffset).toFixed(3)),
                end:   parseFloat((w.end   + chunkStartOffset).toFixed(3)),
                chunk_audio_start_ms: Math.round(chunkStartOffset * 1000),
              }));
              chunkAudioOffsetRef.current = chunkStartOffset;
            }

            const payload = { ...segment, words: absoluteWords };
            
            // Define callback to trigger reprocess after message insert completes
            // Define callback to trigger reprocess after message insert completes
            const onMessageInsertSuccess = () => {
              if (reprocessEnabledRef.current) {
                const effectiveConversationId = conversationId || conversationIdFromUrl || getCookie('conversation_id');
                const viewerDepsLocal = {
                  socketRef,
                  isPausedRef,
                  conversationId: effectiveConversationId,
                  conversationIdFromUrl,
                  previousConversationId,
                };
                reprocessMessage({
                  conversationId: effectiveConversationId,
                  sessionId: sessionId || localStorage.getItem('session_id'),
                  divId: payload.div_id,
                  sequenceId: payload.sequence_id,
                  originalText: payload.text,
                  baseUrl,
                  reprocessEndpoint: REPROCESS,
                  authIdToken,
                  setTranscriptSegments,
                  setPersistentSegments,
                  sendSegmentToViewers,
                  viewerDeps: viewerDepsLocal,
                  highlightColor: '#0066cc', // Blue color for web
                }).then((reprocessData) => { 
                  if (reprocessData?.prev_div_corrected && reprocessData?.prev_div_id) {
                    applyPrevDivCorrection({
                      reprocessApiResponse: reprocessData,
                      setTranscriptSegments,
                      setPersistentSegments,
                      sendSegmentToViewers,
                      viewerDeps: viewerDepsLocal,
                      highlightColor: '#0066cc',
                    });

                    // Send sentence boundary correction to viewers
                    const effectiveConversationId = conversationId || conversationIdFromUrl || getCookie('conversation_id');
                    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN && !isPausedRef.current) {
                      const boundaryPayload = {
                        action: 'segment',
                        conversation_id: effectiveConversationId,
                        text: reprocessData.prev_div_corrected,
                        is_partial: false,
                        is_corrected: true,
                        div_id: reprocessData.prev_div_id,
                        sequence_id: 0,
                        prev_div_id: reprocessData.prev_div_id,
                        prev_div_corrected: reprocessData.prev_div_corrected,
                        create_timestamp: getFormattedTimestamp(new Date()),
                      };
                      socketRef.current.send(JSON.stringify(boundaryPayload));

                      logger.info('Sent sentence boundary correction to viewers', {
                        component: COMPONENT_NAME,
                        action: 'SEND_BOUNDARY_CORRECTION_TO_VIEWERS',
                        userId: getAuthenticatedUserId(),
                        context: {
                          prevDivId: reprocessData.prev_div_id,
                          effectiveConversationId,
                          prevDivCorrectedLength: reprocessData.prev_div_corrected.length,
                          timestamp: Date.now()
                        }
                      });
                    }
                  }
                }).catch((err) => {
                  console.warn('[REPROCESS] Unhandled error:', err?.message || err);
                });
              }
            };
            
            const didSend = sendMessageToBackend(payload, onMessageInsertSuccess);
            if (didSend && !startNewParagraph) {
              const oldSequenceId = sequenceMapRef.current[div_id];
              // Increment for the NEXT segment (this final segment keeps current sequence_id)
              sequenceMapRef.current[div_id] = sequence_id + 1;
              newSequenceMapUpdate = newSequenceMapUpdate || {};
              newSequenceMapUpdate[div_id] = sequence_id + 1;
            }
            
            sendSegmentToViewers(res.text,false,null,segment.div_id,segment.sequence_id,segment.create_timestamp,
            {
              socketRef,
              isPausedRef,
              conversationId,
              conversationIdFromUrl,
              previousConversationId
            },
            false,
            paragraphStartTimerRef.current
          );
            return [...prev.filter(seg => seg.final), segment];
          });
          if (newDivIdForState) {
            setCurrentDivId(newDivIdForState);
          }
          if (newSequenceMapUpdate) {
            setSequenceMap(map => ({ ...map, ...newSequenceMapUpdate }));
          }
        }

  }

  const startTranscriptionStreamWithToken = async (token, model = null) => {
    // Don't start transcription if session has been stopped
    if (userInitiatedStopRef.current || window.isStoppingSession) {
      return;
    }
    
    // Don't start if status indicates session is not active
    const currentStatus = statusRef.current;
    if (currentStatus === "Stopping..." || currentStatus === "Stopped" || currentStatus === "Processing..." || currentStatus === "Ready") {
      return;
    }
    if (assemblySocketRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    if (!token) {
      logger.error('No token provided for transcription connection', {
        component: COMPONENT_NAME,
        action: 'TRANSCRIPTION_START_NO_TOKEN',
        userId: getAuthenticatedUserId(),
        context: {
          conversationId: conversationId || getCookie('conversation_id'),
          sessionId,
          transcriptionModel: model || getStoredModel() || null,
          storedTokenExists: !!getCookie('transcription_token'),
          tokenExpiresAt: getCookie('token_expires_at') || null,
          tokenStatus: getCookie('token_status') || null,
          isLive,
          isPaused: isPausedRef.current,
          webSocketConnected: webSocketConnectedRef.current
        }
      });
      console.error('❌ No token provided for AssemblyAI connection');
      setStatus("Error: No token available");
      return;
    }

     // Use provided model or get from stored cookie
    const transcriptionModel = model || getStoredModel();
    
    if (assemblySocketRef.current) {
      assemblySocketRef.current.close();
    }
    
    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

      try {
        const {serviceName,socketUrl,socket: transcriptionSocket} = createTranscriptionSocket(token,transcriptionModel,deepgramWsBaseUrl,assemblyWsBaseUrl,
        configDetails
      );
      setStatus(`Connecting to ${serviceName}...`);

      const assemblySocket = transcriptionSocket;
      assemblySocketRef.current = assemblySocket;
      const savedAudioS = parseFloat(getCookie('recorded_audio_duration_s') ?? '0') || 0;
      const writtenAt = parseInt(getCookie('recorded_audio_duration_written_at') ?? '0', 10) || 0;
      const isFreshPostRefreshCookie = savedAudioS > 0 && writtenAt > 0 && (Date.now() - writtenAt) < 5000;
      if (isFreshPostRefreshCookie) {
        chunkAudioOffsetRef.current = savedAudioS;
      } else {
        chunkAudioOffsetRef.current = 0;
      }
      // Always clear both cookies after reading — a stale cookie must never pollute a future reconnect.
      deleteCookie('recorded_audio_duration_s');
      deleteCookie('recorded_audio_duration_written_at');
      const effectiveConversationId = conversationId || conversationIdFromUrl || getCookie('conversation_id') || localStorage.getItem('conversation_id');
      setupTranscriptionSocketHandlers(
        assemblySocket,
        {
          socketUrl,configDetails,
          conversationId: effectiveConversationId,
          sessionId,assemblySocketRef,isPausedRef,isReconnectingRef,reconnectionAttemptsRef,reconnectionTimeoutRef,
          audioInitializationRef,keepAliveIntervalRef,reconnectStartedAtRef,userInitiatedStopRef,statusRef,lastKnownTimerRef,streamRef,audioContextRef,
          analyserRef,workletNodeRef,audioChunksRef,mediaRecorderRef,audioWorkletActiveRef,speakingRef,audioQueueRef,socketRef,
          lastAudioSentRef,lastMicActivityRef,lastAudioDroppedRef,droppedAudioTotalMsRef,transcriptionWatchdogRef,
          startTranscriptionWatchdog,stopTranscriptionWatchdog,
          sendAudioChunk,setStatus,setTimer,setIsReconnecting,setShowMicLoadingModal,setS3UploadStatus,setS3AudioRecorder,getCookie,
          setCookie,getStoredModel,getAuthenticatedUserId,calculateElapsedTime,saveRecordingStartTime,saveRecordingStatus,
          flushAudioQueue,startAudioS3Recording,setupS3AudioRecording,initializeAudioStream,setupWorkletMessageHandler,handleAssemblyDisconnection:
          handleAssemblyDisconnectionWrapper,
          forceAssemblyReconnect: () => forceAssemblyReconnect(buildForceReconnectDeps())
        }
      );
      setupTranscriptMessageHandler(assemblySocket,
      {getStoredModel,normalizeTranscriptMessage,handleTranscriptMessage
      }
    );

    } catch (error) {
      logger.error('Failed to start transcription stream', {
        component: COMPONENT_NAME,
        action: 'TRANSCRIPTION_START_FAILED',
        userId: getAuthenticatedUserId(),
        context: {
          conversationId: conversationId || getCookie('conversation_id'),
          transcriptionModel: transcriptionModel || getStoredModel(),
          sessionId
        },
        error
      });
      console.error('❌ [startAssemblyStreamWithToken] Error:', error);
      setStatus("Error starting stream");
    }
  };


  // Initialize WebSocket connection - MUST be after startTranscriptionStreamWithToken is defined
  useWebSocketConnection({
    sessionId,
    conversationId,
    conversationIdFromUrl,
    userId,
    socketRef,
    assemblySocketRef,
    userInitiatedStopRef,
    wsConnectLatchRef,
    persistentDataRequestedRef,
    webSocketConnectedRef,
    isNewSessionRef,
    websocketBaseUrl,
    getAuthenticatedUserId,
    requestToken,
    handlePersistentData,
    startTranscriptionStreamWithToken,
    startTokenExpiryTimer,
    getSessionIdForConversation,
    setSessionId,
    setDateString,
    setTimeString,
    showPlanWarningToast,
    schedulePlanCountdownFromPayload,
    handlePlanLimitReached,
    scheduleTimeLimitDisconnect,
    reconnectWebSocketLocal,
    isLive,
  });

  // Alias for backward compatibility
  const startAssemblyStream = () =>
  startTranscriptionStream({userInitiatedStopRef,assemblySocketRef,getStoredToken,startTranscriptionStreamWithToken,setStatus});


  const formatTimer = (seconds) => {
    const totalSeconds = Math.floor(seconds);
    
    // Before conversation starts (not live and timer is 0), show "0:00"
    if (!isLive && totalSeconds === 0) {
      return "0:00";
    }
    
    // For record page, always show MM:SS format
    if (totalSeconds < 3600) {
      const m = Math.floor(totalSeconds / 60);
      const s = totalSeconds % 60;
      return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    
    // 1 hour or more - show HH:MM:SS
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  async function handleStart() {
    const userId = getAuthenticatedUserId();
    const startTimestamp = Date.now();
    
    logger.info('Recording session start initiated', {
      component: COMPONENT_NAME,
      action: 'CLICK_START',
      userId,
      context: {
        isLive,
        isPaused,
        hasExistingSession: !!sessionId,
        existingSessionId: sessionId || null,
        existingConversationId: conversationId || null,
        keywordsInput: keywordsInput || null,
        transcriptSegmentsCount: transcriptSegments?.length || 0,
        browserOnline: navigator.onLine,
        startTimestamp
      }
    });

    // Don't start if session is being stopped from TopNavbar
    if (window.isStoppingSession) {
      logger.warn('Recording start blocked - session stop in progress', {
        component: COMPONENT_NAME,
        action: 'START_BLOCKED',
        userId,
        context: { reason: 'session_stop_in_progress' }
      });
      return;
    }
    
    if (isStartingRef.current) {
      logger.warn('Recording start blocked - already in progress', {
        component: COMPONENT_NAME,
        action: 'START_BLOCKED',
        userId,
        context: { reason: 'already_starting' }
      });
      return;
    }
    isStartingRef.current = true;
    
    // Mark this as a new session for proper token request
    isNewSessionRef.current = true;
    sessionStorage.setItem('session_creation_flag', 'true');

    // Show microphone loading modal
    setLoadingModalType('starting');
    setShowMicLoadingModal(true);
  
  // CRITICAL: Disconnect and close ALL existing connections PROPERLY
  if (socketRef.current) {
    try {
      socketRef.current.close();
      socketRef.current = null;
      // webSocketConnectedRef.current = false;
      logger.debug('Main WebSocket closed during session start', {
        component: COMPONENT_NAME,
        action: 'WEBSOCKET_CLEANUP',
        userId
      });
    } catch (e) {
      logger.error('Failed to close main WebSocket during start', {
        component: COMPONENT_NAME,
        action: 'WEBSOCKET_CLEANUP_FAILED',
        userId,
        error: e
      });
      console.warn('⚠️ Error closing main WebSocket:', e);
    }
  }
  
  if (assemblySocketRef.current) {
    try {
      assemblySocketRef.current.close();
      assemblySocketRef.current = null;
    } catch (e) {
      console.warn('⚠️ Error closing AssemblyAI WebSocket:', e);
    }
  }
 // Clear ALL cookies and session state
  const cookiesToClear = [
    'recording_start_time', 
    'recording_status', 
    'is_recording_paused',
    'pause_start_time', 
    'total_pause_duration', 
    'last_activity_time', 
    'transcription_token', 
    'transcription_model',
    'token_issued_at', 
    'token_expires_at', 
    'token_status'
  ];

  cookiesToClear.forEach(cookie => {
    deleteCookie(cookie);
  });
  localStorage.removeItem('session_id');
  localStorage.removeItem('session_status');
 
  setConversationId(null);
  setConversationIdFromUrl(null);
  setTranscriptSegments([]);
  setPersistentSegments([]);
  setCurrentDivId(null);
  currentDivIdRef.current = null;
  setSequenceMap({});
  sequenceMapRef.current = {};
  lastSentTextRef.current = {};
  processedMessagesRef.current.clear();
  setPaginationMeta({ totalDivs: 0, firstPageNumber: 0, lastPageNumber: 0, currentPageNumber: 0 });
  setCanLoadMore(false);
  
  resetForNewSession();
  isPausedRef.current = false;
  audioChunksRef.current = [];
  setS3UploadStatus("");
  setTimer(0);
  await new Promise(resolve => setTimeout(resolve, 200));
  
  // Generate new session
  const newSessionId = generateNewSessionId();
  try {
    const userId = getAuthenticatedUserId();
    const authIdToken = await getAuthIdToken();
    const sessionInitializePayload = {
      user_id: userId,
      session_id: newSessionId,
      path: `${userId}_${newSessionId}.webm`,
      time_stamp: new Date().toISOString()
    };
    const response = await fetch(`${baseUrl}${CONVERSATION_INITIATED}`, {
      method: 'POST',
      headers: { "Content-Type": "application/json", "Authorization": authIdToken },
      body: JSON.stringify(sessionInitializePayload)
    });
    
    const result = await response.json();
    
    // Wrapper function to handle the new modal type signature
    const showSubscriptionModal = (modalType = 'expired') => {
      setSubscriptionModalType(modalType);
      setShowSubscriptionExpiredModal(true);
    };
    
    // Check for subscription expired error FIRST, even if response is not ok
    if (checkSubscriptionExpired(result, showSubscriptionModal)) {
      // Hide loading modal and reset state before showing subscription modal
      setShowMicLoadingModal(false);
      isStartingRef.current = false;
      isNewSessionRef.current = false;
      sessionStorage.removeItem('session_creation_flag');
      return; // Don't throw error, just return
    }
    
    // Only throw error for other types of failures
    if (!response.ok) {
      throw new Error(`Session initialize failed: ${response.status} ${response.statusText}`);
    }
    
    if (result && result.conversation_id) {
    const newConversationId = result.conversation_id;

  setCookie("session_id", newSessionId);
  setCookie("original_session_id", newSessionId);
  localStorage.setItem("session_id", newSessionId);
  
  setCookie("conversation_id", newConversationId);
  localStorage.setItem("conversation_id", newConversationId);
  setCookie("conversation_active", "true"); 
  
  setLiveSessionCookies(newSessionId);
  

  setSessionId(newSessionId);
  setConversationId(newConversationId);
  setConversationIdFromUrl(newConversationId);
  setPreviousConversationId(null);
  
  const now = Date.now();
  saveRecordingStartTime(now);
  saveTimerToStorage(newConversationId, 0);
  deleteCookie('recorded_audio_duration_s');
  deleteCookie('recorded_audio_duration_written_at');
  chunkAudioOffsetRef.current = 0;
  const shareUrl = `${window.location.origin}/watch/${newConversationId}`;
  setShareLink(shareUrl);
  
  logger.info('Recording session created successfully', {
    component: COMPONENT_NAME,
    action: 'SESSION_CREATED',
    userId,
    context: {
      conversationId: newConversationId,
      sessionId: newSessionId,
      shareUrl,
      creationDurationMs: Date.now() - startTimestamp,
      keywordsInput: keywordsInput || null,
    }
  });
  // Navigate to new conversation URL
  await new Promise((resolve) => setTimeout(resolve, 150));
  navigate(`/record/${newConversationId}`);
  
  isStartingRef.current = false;
  return;
    } else {
      throw new Error('No conversation_id in response');
    }
  } catch (error) {
    logger.error('Recording session start failed', {
    component: COMPONENT_NAME,
    action: 'START_FAILED',
    userId: getAuthenticatedUserId(),
    context: {
      errorMessage: error.message,
      sessionId: sessionId
    },
    error
  });
  console.error('[handleStart] ERROR during start', error);
  setStatus(`Error: ${error.message}`);
  setShowMicLoadingModal(false); // Hide loading modal on error
  isStartingRef.current = false;
  // Clean up flags on error
  isNewSessionRef.current = false;
  sessionStorage.removeItem('session_creation_flag');
  return;
  }
};

  const handlePause = () => {
    const userId = getAuthenticatedUserId();
    logger.info('Recording pause initiated', {
      component: COMPONENT_NAME,
      action: 'CLICK_PAUSE',
      userId,
      context: {
        isLive,
        isPaused,
        timer,
        conversationId: conversationId || getCookie('conversation_id'),
        sessionId
      }
    });
    if (isLive && !isPaused) {
      // Store current timer value before pausing
      lastKnownTimerRef.current = timer;
      
      pauseTimestampRef.current = Date.now();
      setIsPaused(true);
      isPausedRef.current = true;
      pauseAudioRecording();
      logger.info('Recording successfully paused', {
        component: COMPONENT_NAME,
        action: 'PAUSE_SUCCESS',
        userId,
        context: {
          timer,
          durationMinutes: (timer / 60).toFixed(2),
          conversationId: conversationId || getCookie('conversation_id'),
          sessionId,
          pauseTimestamp: pauseTimestampRef.current,
          transcriptSegmentsCount: transcriptSegments?.length || 0,
          transcriptionModel: getStoredModel() || null,
          webSocketConnected: webSocketConnectedRef.current,
          assemblySocketOpen: assemblySocketRef.current?.readyState === WebSocket.OPEN
        }
      });
      
      // Save pause state for session restoration
      savePauseState(true);
      saveRecordingStatus('Paused');

      // Start KeepAlive interval to keep WebSocket connection alive during pause
       // Only start KeepAlive for Deepgram - AssemblyAI doesn't need it
      const currentModel = getStoredModel();
      if (currentModel === configDetails.TRANSCRIPTION_MODEL_DEEPGRAM) {
        if (keepAliveIntervalRef.current) {
          clearInterval(keepAliveIntervalRef.current);
          keepAliveIntervalRef.current = null;
        }
          keepAliveIntervalRef.current = setInterval(() => {
              if (assemblySocketRef.current && assemblySocketRef.current.readyState === WebSocket.OPEN) {
                try {
                  assemblySocketRef.current.send(JSON.stringify({ type: "KeepAlive" }));
                } catch (error) {
                  console.warn('⚠️ [KeepAlive] Failed to send KeepAlive:', error);
                }
              }
            }, 3000);
          }

      // Send conversation_live_status payload when paused
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        const userId = getAuthenticatedUserId();
        // Try to get conversation_id from state, URL, or cookies
        let effectiveConversationId = conversationId;
        if (!effectiveConversationId || effectiveConversationId === 'null') {
          // Fallback: try to extract from URL again
          const pathSegments = window.location.pathname.split('/');
          const urlConvId = pathSegments[pathSegments.length - 1];
          if (urlConvId && urlConvId !== 'record' && urlConvId.length > 10) {
            effectiveConversationId = urlConvId;
          }
        }
        // If still not found, do not send segment
        if (!effectiveConversationId || effectiveConversationId === 'null') {
          logger.warn('WebSocket pause status not sent - invalid conversation_id', {
            component: COMPONENT_NAME,
            action: 'PAUSE_STATUS_NOT_SENT',
            userId,
            context: {
              reason: 'invalid_conversation_id',
              conversationId,
              conversationIdFromUrl,
              cookieConversationId: getCookie('conversation_id')
            }
          });
          console.warn('[handlePause] No valid conversation_id, pause status not sent');
          return;
        }
        if (!userId) {
          logger.warn('WebSocket pause status not sent - invalid user_id', {
            component: COMPONENT_NAME,
            action: 'PAUSE_STATUS_NOT_SENT',
            context: { reason: 'invalid_user_id' }
          });
          console.warn('[handlePause] No valid user_id, pause status not sent');
          return;
        }
        const payload = {
          action: 'conversation_live_status',
          conversation_id: effectiveConversationId,
          user_id: userId,
          current_status: 'PAUSED',
          time: timer
        };
        logger.info('Sending pause status via WebSocket', {
          component: COMPONENT_NAME,
          action: 'SEND_PAUSE_STATUS',
          userId,
          context: {
            conversationId: effectiveConversationId,
            time: timer
          }
        });
        socketRef.current.send(JSON.stringify(payload));
      }

      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }

        // Pause S3 audio recording
      if (s3AudioRecorder && s3AudioRecorder.state === 'recording') {
        s3AudioRecorder.pause();
      }
      
      sendSegmentToViewers('',false,'pause',currentDivId,sequenceMapRef.current[currentDivId] || 0,getFormattedTimestamp(new Date()),
      {
          socketRef,
          isPausedRef,
          conversationId,
          conversationIdFromUrl,
          previousConversationId
        }
      );
    }
  };

  const handleResume = () => {
    const userId = getAuthenticatedUserId();
    logger.info('Recording resume initiated', {
      component: COMPONENT_NAME,
      action: 'CLICK_RESUME',
      userId,
      context: {
        isLive,
        isPaused,
        timer,
        conversationId: conversationId || getCookie('conversation_id'),
        sessionId
      }
    });
    if (isLive && isPaused) {
      // First, calculate what the timer SHOULD be before we change any state
      const pauseStartTime = parseInt(getCookie('pause_start_time'));
      const startTime = parseInt(getCookie('recording_start_time'));
      const totalPauseDuration = parseInt(getCookie('total_pause_duration')) || 0;
      
      let correctTimerValue = lastKnownTimerRef.current;
      
      if (startTime && pauseStartTime) {
        // Calculate how long we were paused
        const pauseDurationMs = Date.now() - pauseStartTime;
        const pauseDurationSeconds = Math.floor(pauseDurationMs / 1000);
        
        // Calculate what the timer should be (time since start minus all pause durations)
        const totalElapsedMs = Date.now() - startTime;
        const newTotalPauseDuration = totalPauseDuration + pauseDurationSeconds;
        correctTimerValue = Math.floor(totalElapsedMs / 1000) - newTotalPauseDuration;
        correctTimerValue = Math.max(0, correctTimerValue);
      }
      
      // Set the correct timer value immediately BEFORE changing pause state
      setTimer(correctTimerValue);
      lastKnownTimerRef.current = correctTimerValue;
      
      // Update state
      setIsPaused(false);
      isPausedRef.current = false;
      resumeAudioRecording();
      // Save resume state for session restoration
      savePauseState(false);
      lastKnownTimerRef.current = calculateElapsedTime();
      setTimer(lastKnownTimerRef.current);
      saveRecordingStatus('🎙️ Listening...');

    // Stop KeepAlive interval when resuming
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
    const pauseDurationSeconds = pauseTimestampRef.current ? Math.floor((Date.now() - pauseTimestampRef.current) / 1000) : 0;
    logger.info('Recording successfully resumed', {
      component: COMPONENT_NAME,
      action: 'RESUME_SUCCESS',
      userId,
      context: {
        timer: correctTimerValue,
        pauseDurationSeconds,
        conversationId: conversationId || getCookie('conversation_id'),
        sessionId,
        transcriptSegmentsCount: transcriptSegments?.length || 0,
        transcriptionModel: getStoredModel() || null,
        webSocketConnected: webSocketConnectedRef.current,
        assemblySocketOpen: assemblySocketRef.current?.readyState === WebSocket.OPEN,
        needsReconnection: pauseDurationSeconds > 110 || !assemblySocketRef.current || assemblySocketRef.current.readyState !== WebSocket.OPEN
      }
    });
    // Send conversationlivestatus payload for LIVE when resuming from PAUSED
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      const userId = getAuthenticatedUserId();
      // Try to get conversation_id from state, URL, or cookies
      let effectiveConversationId = conversationId || conversationIdFromUrl || getCookie('conversation_id');
      if (!effectiveConversationId || effectiveConversationId === 'null') {
        // Fallback: try to extract from URL again
        const pathSegments = window.location.pathname.split('/');
        const urlConvId = pathSegments[pathSegments.length - 1];
        if (urlConvId && urlConvId !== 'record' && urlConvId.length > 10) {
          effectiveConversationId = urlConvId;
        }
      }
      // If still not found, do not send segment
      if (!effectiveConversationId || effectiveConversationId === 'null') {
        logger.warn('WebSocket resume status not sent - invalid conversation_id', {
          component: COMPONENT_NAME,
          action: 'RESUME_STATUS_NOT_SENT',
          userId,
          context: {
            reason: 'invalid_conversation_id',
            conversationId,
            conversationIdFromUrl,
            cookieConversationId: getCookie('conversation_id')
          }
        });
        console.warn('[handleResume] No valid conversation_id, resume status not sent');
        return;
      }
      if (!userId) {
        logger.warn('WebSocket resume status not sent - invalid user_id', {
          component: COMPONENT_NAME,
          action: 'RESUME_STATUS_NOT_SENT',
          context: { reason: 'invalid_user_id' }
        });
        console.warn('[handleResume] No valid user_id, resume status not sent');
        return;
      }
      // CRITICAL: Prevent sending LIVE status to a previously stopped conversation
      if (previousConversationId && effectiveConversationId === previousConversationId) {
        return;
      }
      const payload = {
        action: 'conversation_live_status',
        conversation_id: effectiveConversationId,
        user_id: userId,
        current_status: 'LIVE',
        time: correctTimerValue
      };
      logger.info('Sending resume status via WebSocket', {
        component: COMPONENT_NAME,
        action: 'SEND_RESUME_STATUS',
        userId,
        context: {
          conversationId: effectiveConversationId,
          time: correctTimerValue
        }
      });
      socketRef.current.send(JSON.stringify(payload));
    }

     // Resume S3 audio recording
    if (s3AudioRecorder && s3AudioRecorder.state === 'paused') {
      s3AudioRecorder.resume();
    }

    // Notify viewers recording resumed
    sendSegmentToViewers('',false,'resume',currentDivId,sequenceMapRef.current[currentDivId] || 0,getFormattedTimestamp(new Date()),
      {
        socketRef,
        isPausedRef,
        conversationId,
        conversationIdFromUrl,
        previousConversationId
      }
    );

    // Check if AssemblyAI needs reconnection
    const now = Date.now();
    const pauseDuration = pauseTimestampRef.current ? (now - pauseTimestampRef.current) / 1000 : 0;
    const isSocketClosed = !assemblySocketRef.current || assemblySocketRef.current.readyState !== WebSocket.OPEN;

    if (pauseDuration > 110 || isSocketClosed) {
      forceAssemblyReconnect(buildForceReconnectDeps());
    }
  }
};

  async function handleStop() {
    // Mark save as in-progress IMMEDIATELY so the beforeunload listener
    // warns the user if they try to close the tab during the save flow.
    saveInProgressRef.current = true;
    userInitiatedStopRef.current = true;
    const userId = getAuthenticatedUserId();
      const stopTimestamp = Date.now();

      let elapsedSeconds = 0;
      try {
        if (typeof calculateElapsedTime === 'function') {
          elapsedSeconds = calculateElapsedTime();
        }
      } catch (e) {
        console.warn('[handleStop] calculateElapsedTime failed, falling back to timer state:', e);
      }
      if (!elapsedSeconds || elapsedSeconds <= 0) {
        elapsedSeconds = lastKnownTimerRef?.current || timer || 0;
      }
      const durationMinutes = (elapsedSeconds / 60).toFixed(2);
      
      const stopConversationId = conversationId || conversationIdFromUrl || getCookie('conversation_id') || localStorage.getItem('conversation_id');
      const stopSessionId = sessionId || getCookie('session_id') || localStorage.getItem('session_id');

      logger.info('Recording stop initiated by user', {
        component: COMPONENT_NAME,
        action: 'CLICK_STOP',
        userId,
        context: {
          timer,
          durationMinutes: parseFloat(durationMinutes),
          conversationId: conversationId || getCookie('conversation_id'),
          sessionId,
          isLive,
          isPaused,
          transcriptSegmentsCount: transcriptSegments?.length || 0,
          persistentSegmentsCount: persistentSegments?.length || 0,
          audioChunksCount: audioChunksRef.current?.length || 0,
          stopTimestamp
        }
      });
      userInitiatedStopRef.current = true;
      clearPlanCountdown();
      if (!planLimitStopInProgressRef.current) {
        clearPlanWarningToast();
      }
      
      if (typeof window !== 'undefined') {
        window.isStoppingSession = true;
      }
      
      // Show loading modal for stopping process
      setLoadingModalType('stopping');
      setShowMicLoadingModal(true);

      // Clear live session cookies as soon as user clicks stop
      clearLiveSessionCookies();
      const sessionCookiesToDelete = [
      'recording_status',
      'session_id',
      'recording_start_time',
      'is_recording_paused',
      'pause_start_time',
      'total_pause_duration',
      'last_activity_time',
      'original_session_id'
    ];
    
    sessionCookiesToDelete.forEach(cookieName => {
      deleteCookie(cookieName);
    });
    
    localStorage.removeItem('session_id');
    localStorage.removeItem('conversation_id');
      
    if (sessionId) {
      localStorage.removeItem(`timer_${sessionId}`);
    }
    if (conversationId) {
      localStorage.removeItem(`timer_${conversationId}`);
    }

    // Immediately set status to non-live to prevent UI showing live state
    setStatus("Stopping...");
    
    // CRITICAL: Clear any pending reconnection attempts FIRST
    if (reconnectionTimeoutRef.current) {
      clearTimeout(reconnectionTimeoutRef.current);
      reconnectionTimeoutRef.current = null;
    }

    // Stop KeepAlive interval on stop
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
    
    // Reset reconnection attempts to prevent future reconnections
    reconnectionAttemptsRef.current = configDetails.MAX_RECONNECTION_ATTEMPTS;
    isReconnectingRef.current = false;
    setIsReconnecting(false);
    
    let audioUploadPromise = null;
    // Always attempt upload if there are audio chunks, regardless of recorder state
    if (mediaRecorderRef.current && (mediaRecorderRef.current.state === 'recording' || mediaRecorderRef.current.state === 'paused')) {
      // If still recording or paused, stop and wait for onstop
      audioUploadPromise = new Promise((resolve) => {
        mediaRecorderRef.current.onstop = async () => {
          if (audioChunksRef.current.length > 0) {
            try {
              const userId = getAuthenticatedUserId();
              const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
              const payload = {
                user_id: userId,
                conversation_id: stopConversationId
              };
              const response = await fetch(`${baseUrl}${configDetails.UPLOAD_AUDIO}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': authIdToken },
                body: JSON.stringify(payload),
              });
              if (!response.ok) {
                throw new Error(`Failed to get presigned URL: ${response.status}`);
              }
              const { presigned_url } = await response.json();
              // Step 2: PUT audio to S3
              // Convert to WAV and upload
              const uploadOk = await putFileToS3(presigned_url, audioBlob, 'audio/wav');
              if (!uploadOk) {
                throw new Error(`S3 upload failed: putFileToS3 returned false`);
              }
              resolve(true);
            } catch (error) {
              resolve(false);
            }
          } else {
            resolve(false);
          }
        };
        mediaRecorderRef.current.stop();
      });
    } else if (audioChunksRef.current && audioChunksRef.current.length > 0) {
      audioUploadPromise = (async () => {
        try {
          const userId = getAuthenticatedUserId();
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
          const payload = {
            user_id: userId,
            conversation_id: conversationId
          };
          const response = await fetch(`${baseUrl}${configDetails.UPLOAD_AUDIO}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': authIdToken },
            body: JSON.stringify(payload),
          });
          if (!response.ok) {
            throw new Error(`Failed to get presigned URL: ${response.status}`);
          }
          const { presigned_url } = await response.json();
          // Step 2: PUT audio to S3
          // Convert to WAV and upload
          const uploadOk = await putFileToS3(presigned_url, audioBlob, 'audio/wav');
          if (!uploadOk) {
            throw new Error(`S3 upload failed: putFileToS3 returned false`);
          }
          return true;
        } catch (error) {
          return false;
        }
      })();
    }
           
    if (!planLimitStopInProgressRef.current && socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      const userId = getAuthenticatedUserId();
      let effectiveConversationId = conversationId || conversationIdFromUrl || getCookie('conversation_id');

      // Validate conversation_id
      if (!effectiveConversationId || effectiveConversationId === 'null') {
        console.warn('[handleStop] No valid conversation_id, stop status not sent');
      } else if (!userId) {
        console.warn('[handleStop] No valid user_id, stop status not sent');
      } else {
        const payload = {
          action: 'conversation_live_status',
          conversation_id: effectiveConversationId,
          user_id: userId,
          current_status: 'STOPPED',
          time: timer
        };
        logger.info('Sending stop status via WebSocket', {
          component: COMPONENT_NAME,
          action: 'SEND_STOP_STATUS',
          userId,
          context: {
            conversationId: effectiveConversationId,
            time: timer
          }
        });
        socketRef.current.send(JSON.stringify(payload));
      }
    }

    stopAssemblyStream({reconnectionTimeoutRef,reconnectionAttemptsRef,assemblySocketRef,workletNodeRef,audioContextRef,streamRef,mediaRecorderRef,userInitiatedStopRef,animationFrameRef,s3AudioRecorder,setStatus,
      stopTranscriptionWatchdog,transcriptionWatchdogRef});
    stopTokenExpiryTimer();

    // Log the time and duration when stop is clicked
    const stopTime = new Date();
    let trackingMinutes = parseFloat(durationMinutes);
    if (!trackingMinutes || isNaN(trackingMinutes) || trackingMinutes <= 0) {
      const fallbackSeconds = lastKnownTimerRef?.current || timer || 0;
      trackingMinutes = parseFloat((fallbackSeconds / 60).toFixed(2));
    }
    const minutes = trackingMinutes.toFixed(2);

    // Send time (in float minutes) to tracking_time API
    try {
      const userId = getAuthenticatedUserId();
      const authIdToken = await getAuthIdToken();
      await fetch(`${trackingUrl}${TRACKING_TIME}`, {
        method: 'POST',
        headers: { "Content-Type": "application/json", "Authorization": authIdToken },
        body: JSON.stringify({ user_id: userId, time: trackingMinutes })
      });
      // Dispatch event to refresh tracking time in sidebar
      window.dispatchEvent(new Event('refresh-tracking-time'));
    } catch (err) {
      console.error('[Record] Error sending time to tracking_time API:', err);
    }

     // Wait for audio upload to complete before calling conversation_completed
      if (audioUploadPromise) {
        await audioUploadPromise;
      }

     try {
      await stopAudioS3Recording();
      logger.info('S3 uploads completed successfully', {
        component: COMPONENT_NAME,
        action: 'S3_UPLOADS_COMPLETED'
      });
    } catch (error) {
      logger.error('S3 stop failed', { 
        component: COMPONENT_NAME, 
        action: 'S3_STOP_FAILED',
        error: error.message 
      });
    }

    // Call CONVERSATION_COMPLETED endpoint
    try {
      const userId = getAuthenticatedUserId();
      const conversationIdToSend = stopConversationId;
      const sessionIdToSend = stopSessionId;
      if (!conversationIdToSend) {
        console.warn('⚠️ [handleStop] No conversation_id found before cleanup');
      }
      
      const sessionCompletePayload = {
        user_id: userId,
        session_id: sessionIdToSend,
        conversation_id: conversationIdToSend,
        path: `${userId}_${sessionIdToSend}.webm`,
        time_stamp: new Date().toISOString(), // Current timestamp in ISO format
        seconds_used: elapsedSeconds
      };

      const authIdToken = await getAuthIdToken();
      const response = await fetch(`${baseUrl}${CONVERSATION_COMPLETED}`, {
        method: 'POST',
        headers: { "Content-Type": "application/json", "Authorization": authIdToken },
        body: JSON.stringify(sessionCompletePayload),
      });

      if (!response.ok) {
        throw new Error(`Session complete failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      logger.info('Recording session completed successfully', {
        component: COMPONENT_NAME,
        action: 'SESSION_COMPLETED',
        userId,
        context: {
          conversationId: conversationIdToSend,
          sessionId,
          durationSeconds: elapsedSeconds,
          durationMinutes: parseFloat(durationMinutes),
          totalProcessingTimeMs: Date.now() - stopTimestamp,
          transcriptSegmentsCount: transcriptSegments?.length || 0,
          persistentSegmentsCount: persistentSegments?.length || 0,
          audioUploaded: !!audioUploadPromise
        }
      });

      // Refresh meta_data and update session_status in localStorage
      try {
        const userId = getAuthenticatedUserId();
        const sessionIdLocal = localStorage.getItem("session_id");
        if (userId && sessionIdLocal) {
          const response = await fetch(`${baseUrl}${configDetails.META_DATA}?user_id=${userId}`,
            { method: 'GET', headers: { 'Content-Type': 'application/json', 'Authorization': authIdToken } }
          );
          const metaData = await response.json();
          const currentSession = metaData.find(m => m.session_id === sessionIdLocal);
          if (currentSession?.status) {
            localStorage.setItem("session_status", currentSession.status);
          }
        }
      } catch (error) {
        console.error("❌ Failed to refresh meta_data after stop:", error);
      }

    } catch (error) {
      if (error && (error.name === 'AbortError' || error.code === 20)) {
        console.warn('handleStop] CONVERSATION_COMPLETED aborted — user left during save. Note will not be saved.');
      } else {
        logger.error('Failed to complete recording session', {
          component: COMPONENT_NAME,
          action: 'SESSION_COMPLETE_FAILED',
          userId: getAuthenticatedUserId(),
          context: {
            conversationId: conversationId || getCookie('conversation_id'),
            sessionId,
            timer
          },
          error
        });
        console.error('[handleStop] Error calling CONVERSATION_COMPLETED:', error);
      }
      // Don't return here - continue with cleanup even if CONVERSATION_COMPLETED fails
    }

    // Notify viewers recording stopped
      sendSegmentToViewers('',false,'stop',currentDivId,sequenceMapRef.current[currentDivId] || 0,getFormattedTimestamp(new Date()),
      {
        socketRef,
        isPausedRef,
        conversationId,
        conversationIdFromUrl,
        previousConversationId
      }
    );

    const performCleanup = () => {      
        mediaRecorderRef.current = null;
        if (audioContextRef.current) {
        try {
          if (audioContextRef.current.state !== 'closed') {
            audioContextRef.current.close();
          }
        } catch (e) {}
        audioContextRef.current = null;
      }
        if (streamRef.current) {
          try { streamRef.current.getTracks().forEach(t => t.stop()); } catch (e) {}
          streamRef.current = null;
        }
      // Clear all session state cookies (including conversation_id, transcription_token, etc.)
      clearSessionState();
      
      //  Double-check all session cookies are deleted
      const sessionCookiesToDelete = [
        'recording_start_time', 'recording_status', 'is_recording_paused',
        'pause_start_time', 'total_pause_duration', 'last_activity_time',
        'original_session_id', 
        'conversation_id',
        'conversation_active', 
        'transcription_token',
        'transcription_model',
        'token_issued_at',
        'token_expires_at', 
        'token_status',
        'session_id' 
      ];
      sessionCookiesToDelete.forEach(cookieName => {
        deleteCookie(cookieName);
      }); 

      if (sessionId) {
        localStorage.removeItem(`timer_${sessionId}`);
      }
      
      // Remove conversation timer if exists
      if (conversationId) {
        localStorage.removeItem(`timer_${conversationId}`);
      }

      // RESET CONTEXT for new session
      resetAfterSessionCompleted();
      // Force context state update to ensure UI reflects stopped state instantly
      setStatus('Ready');
      setDateString('');
      setTimeString('');
      setIsPaused(false);
      isPausedRef.current = false;
      audioChunksRef.current = [];
      mediaRecorderRef.current = null;
      setS3AudioRecorder(null);
      setS3AudioChunks([]);
      setS3UploadStatus('');
      // Reset connection tracking flags
      persistentDataRequestedRef.current = false;
      webSocketConnectedRef.current = false;
      

      // Stop token expiry timer
      stopTokenExpiryTimer();
      
      // Hide loading modal before navigation
      setShowMicLoadingModal(false);
      
      // Reset global and ref flags for next session
      if (typeof window !== 'undefined') {
        window.isStoppingSession = false;
      }
      planLimitStopInProgressRef.current = false;
      userInitiatedStopRef.current = false;
      saveInProgressRef.current = false;

      // Navigate home
      navigate(routesfe.HOME);
    };

    if (isAutoDeleteEnabled()) {
      // If auto-delete is enabled, skip audio upload and summary but still do cleanup
      setStatus('Auto-delete is enabled. Note will not be saved.');
      // Add a small delay to show the message, then cleanup and navigate
      setTimeout(() => {
        performCleanup();
      }, 1500);
      return;
    }
    // Since CONVERSATION_COMPLETED handles summary generation, we just perform cleanup
    try {
      setStatus("Completing session...");
      
      // Perform cleanup and navigate home
      performCleanup();
      
    } catch (error) {
      console.error('❌ [handleStop] Error during stop process:', error);
      setStatus("Error occurred");
      // Still perform cleanup even on error
      setTimeout(() => {
        performCleanup();
      }, 2000);
    }
     // Final cleanup: Delete recording_status cookie one more time at the end
    deleteCookie('recording_status');
  };

   // Start S3 recording when live recording starts
  useEffect(() => {
    if (isLive && s3AudioRecorder && s3AudioRecorder.state === 'inactive' && !isPaused) {
       if (s3AudioRecorder.stream && s3AudioRecorder.stream === streamRef.current && s3AudioRecorder.stream.active) {
        try {
          s3AudioRecorder.start(100); // Collect data every 100ms
          setS3UploadStatus('Recording audio for S3...');
        } catch (error) {
          console.error('[S3 Start Effect] ❌ Failed to start recorder:', error);
          setS3UploadStatus(`S3 Recording Error: ${error.message}`);
        }
      } else {
        console.warn('[S3 Start Effect] ⚠️ Skipping start - stream mismatch or inactive:', {
          hasStream: !!s3AudioRecorder.stream,
          streamsMatch: s3AudioRecorder.stream === streamRef.current,
          streamActive: s3AudioRecorder.stream ? s3AudioRecorder.stream.active : false
        });
        
        // Clean up the stale recorder
        setS3AudioRecorder(null);
        setS3UploadStatus('');
      }
    } 
  }, [isLive, s3AudioRecorder, isPaused]);



  // At the end of Record.jsx - Update the cleanup useEffect
useEffect(() => {
  return () => {
    // Check if user is navigating or truly stopping
    const recordingStatus = getCookie('recording_status');
    const isActiveSession = getCookie('conversation_active') === 'true';
    
    if (!userInitiatedStopRef.current && (isActiveSession || recordingStatus === '🎙️ Listening...' || recordingStatus === 'Paused')) {
      return; // Exit cleanup without closing ANYTHING
    }

    // Stop the transcription watchdog on unmount - recording is ending
    stopTranscriptionWatchdog({ transcriptionWatchdogRef });

    if (assemblySocketRef.current) {
      try {
        if (assemblySocketRef.current) {
          const currentState = assemblySocketRef.current.readyState;
          if (currentState === WebSocket.OPEN || currentState === WebSocket.CLOSING) {
            assemblySocketRef.current.close();
            assemblySocketRef.current = null;
          } else if (currentState === WebSocket.CONNECTING) {
            assemblySocketRef.current.onopen = null;
            assemblySocketRef.current.onclose = null;
            assemblySocketRef.current.onerror = null;
            assemblySocketRef.current.onmessage = null;
            assemblySocketRef.current = null;
          }
        }
      } catch (e) {
        console.warn('Error closing Assembly socket', e);
      }
      assemblySocketRef.current = null;
    }
    
    // Close backend WebSocket only on explicit stop
    if (socketRef.current) {
      try {
        if (socketRef.current.readyState !== WebSocket.CLOSED) {
          socketRef.current.close();
        }
      } catch (e) {
        console.warn('Error closing backend socket', e);
      }
      socketRef.current = null;
    }
    
    // Stop microphone MediaStream/streams
    if (streamRef.current) {
      try {
        streamRef.current.getTracks().forEach(track => track.stop());
      } catch (e) {
        console.warn('Error stopping microphone tracks', e);
      }
      streamRef.current = null;
    }
    
    if (audioContextRef.current) {
      try {
        if (audioContextRef.current.state !== 'closed') {
          audioContextRef.current.close();
        }
      } catch (e) {
        console.warn('Error closing AudioContext', e);
      }
      audioContextRef.current = null;
    }
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    // Clear KeepAlive interval on unmount
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
  };
}, []);

  return (
    <div className="d-flex flex-column" style={{ background: '#FFFFFF', height: '100vh', minHeight: '700px' }}>
      {planWarningToast && (() => {
        const msg = String(planWarningToast).toLowerCase();
        let isUrgent = false;

        const hasSecondsOnly = /\b\d+\s*(seconds?|secs?|s)\b/.test(msg) && !/\bminute|\bmin\b/.test(msg);
        const minMatch = msg.match(/(\d+)\s*(minutes?|mins?)/);
        const minutes = minMatch ? parseInt(minMatch[1], 10) : null;
        const comboMatch = msg.match(/(\d+)\s*(?:minutes?|mins?)\s*(\d+)\s*(?:seconds?|secs?)/);

        if (hasSecondsOnly) isUrgent = true;
        if (minutes !== null && minutes <= 1) isUrgent = true;
        if (comboMatch && parseInt(comboMatch[1], 10) <= 1) isUrgent = true;

        const bg = isUrgent
          ? 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'
          : 'linear-gradient(135deg, #3B82F6 0%, #1A73E8 100%)';
        const glow = isUrgent
          ? '0 0 0 6px rgba(220, 38, 38, 0.12), 0 14px 36px rgba(220, 38, 38, 0.38)'
          : '0 0 0 6px rgba(26, 115, 232, 0.10), 0 14px 36px rgba(26, 115, 232, 0.30)';

        return (
          <div
            role="status"
            aria-live="polite"
            className="plan-warning-toast"
            style={{
              background: bg,
              boxShadow: glow,
              position: 'fixed',
              zIndex: 10000
            }}
          >
            {planWarningToast}
          </div>
        );
      })()}
      {/* Loading Overlay for Page Navigation/Refresh */}
      {isPageLoading && (
        <div className="page-loading-overlay">
          <div className="spinner-border page-loading-spinner" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="page-loading-text">Loading your recording session...</p>
        </div>
      )}
      {/* ===== PAGE CONTENT ===== */}
      <div className="container-fluid flex-grow-1 d-flex flex-column align-items-center justify-content-start record-main-container" style={{ minHeight: '100vh', height: 'auto', padding: 0, width: '100%', paddingBottom: '80px', position: 'relative' }}>
        <div className="glass-card w-100" style={{ maxWidth: '100%', borderRadius: '0px', margin: 0, height: '100%', display: 'flex', flexDirection: 'column', filter: isPageLoading ? 'blur(4px)' : showMicLoadingModal ? 'blur(4px)' : 'none', transition: 'filter 0.3s ease' }}>
          <div className="mb-2 px-4 pb-4 pt-4 record-header-section" style={{ minHeight: 'unset', height: 'auto', maxHeight: '35vh', overflowY: 'auto', flexShrink: 0 }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="fw-bold mb-0 text-dark d-flex align-items-center" style={{ fontSize: '1.5rem', letterSpacing: '-0.02em' }}>
                <BsMicFill className="me-2" style={{ color: '#1a73e8' }} />
                Note Recording
              </h2>
              <div className="d-flex gap-2 align-items-center">
                {/* Reprocess Toggle Switch using Bootstrap — only visible once the conversation has started */}
                <div className="d-flex align-items-center gap-2">
                  <Form.Check
                    type="switch"
                    id="enable-reprocess-switch"
                    label="Enable Reprocess"
                    checked={reprocessEnabled}
                    onChange={() => {
                      toggleReprocess();
                      logger.info('Reprocess toggle clicked', {
                        component: COMPONENT_NAME,
                        action: 'CLICK_REPROCESS_TOGGLE',
                        context: {
                          newState: !reprocessEnabled
                        }
                      });
                    }}
                    title={reprocessEnabled ? 'Auto-correct enabled' : 'Enable auto-correct'}
                  />
                </div>
                {(conversationId || conversationIdFromUrl || isLive) && (
                <button
                  className="btn btn-outline-primary d-flex align-items-center share-button-mobile"
                  style={{ borderRadius: '50%', width: 44, height: 44 }}
                  title="Share Conversation"
                  onClick={() => {
                    logger.info('Share modal opened', {
                      component: COMPONENT_NAME,
                      action: 'CLICK_SHARE',
                      userId: getAuthenticatedUserId(),
                      context: {
                        conversationId: conversationId || conversationIdFromUrl,
                        hasShareLink: !!shareLink
                      }
                    });
                    setShowShareModal(true);
                  }}
                >
                  <BsShareFill style={{ fontSize: '1.4rem' }} />
                </button>
                )}
              </div>
            </div>
            {!isLive && (
            <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2 mb-2 keywords-input-container">
              <input
                type="text"
                className="form-control rounded-3"
                style={{ fontSize: 16, padding: "12px 16px", fontWeight: 500 }}
                placeholder="Enter keywords to boost them e.g. AI, React, Meeting"
                value={keywordsInput}
                 onChange={e => setKeywordsInput(e.target.value)}
              />
            </div>
            )}
            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2 gap-md-3 mb-2 info-section" style={{ color: '#1A73E8', fontSize: '0.95rem', fontWeight: '500' }}>
              <span className="d-flex align-items-center gap-2">
                <BsClockFill />
                <span>{dateString}, {timeString}</span>
              </span>
              <span className="d-flex align-items-center gap-2">
                <BsPersonFill />
                <span>Owner: {getUserName()}</span>
              </span>
            </div>
          </div>
          <hr className="my-2 mx-4" style={{ border: 'none', height: '1px', background: 'linear-gradient(90deg, transparent, #1A73E8 40%, transparent)' }} />
          <div
           className="transcript-area mb-3 mx-4 rounded-4 p-3 overflow-auto"
            style={{ flex: '1 1 auto', minHeight: '200px', maxHeight: 'calc(100vh - 400px)' }}
            ref={transcriptContainerRef}
          >
            {transcriptSegments.length === 0 && persistentSegments.length === 0 ? (
              <div className="text-center mt-5" style={{ color: '#6c757d' }}>
                <h5 className="mb-2" style={{ color: '#495057', fontWeight: '600' }}>Ready to capture your thoughts</h5>
                <p className="mb-3" style={{ fontSize: '1.1rem', color: '#6c757d' }}>Start recording to see your live transcript appear here.</p>
                {keywordsInput && (
                  <div className="d-inline-block px-3 py-2 rounded-pill" style={{ background: 'rgba(26,115,232,0.08)', color: '#1A73E8', fontSize: '0.9rem', fontWeight: '500' }}>
                    <BsTagsFill className="me-2" />
                    Keywords: <span className="fw-bold">{keywordsInput}</span>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Loading indicator for older segments */}
                {isLoadingOlderSegments && (
                  <div className="text-center mb-3" style={{ color: '#1a73e8' }}>
                    <div className="d-inline-block px-3 py-2 rounded-pill" style={{ background: 'rgba(26,115,232,0.08)', fontSize: '0.9rem', fontWeight: '500' }}>
                      <div className="spinner-border spinner-border-sm me-2" role="status" style={{ width: '1rem', height: '1rem' }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Loading older messages...
                    </div>
                  </div>
                )}
                
                {/* Show scroll hint if more content available */}
                {canLoadMore && !isLoadingOlderSegments && persistentSegments.length > 0 && (
                  <div className="text-center mb-2" style={{ color: '#6C757D', fontSize: '0.85rem' }}>
                    ↑ Scroll up to load older messages
                  </div>
                )}
                
                {getDisplaySegments(persistentSegments,transcriptSegments,currentDivIdRef).map((segmentGroup, groupIdx) => {
                  const firstSeg = segmentGroup[0];

                  // Use timerAtCreation if present; fall back to deriving from create_timestamp vs recording start
                  let paragraphTimerSecs = firstSeg?.timerAtCreation ?? null;
                  if ((paragraphTimerSecs === null || paragraphTimerSecs === undefined) && firstSeg?.create_timestamp) {
                    const recordingStart = parseInt(getCookie('recording_start_time'));
                    if (recordingStart) {
                      paragraphTimerSecs = Math.max(0, Math.floor((new Date(firstSeg.create_timestamp) - recordingStart) / 1000));
                    }
                  }

                  // Format as M:SS
                  const timeLabel = (paragraphTimerSecs != null && !isNaN(paragraphTimerSecs))
                    ? `${Math.floor(paragraphTimerSecs / 60)}:${String(Math.floor(paragraphTimerSecs % 60)).padStart(2, '0')}`
                    : null;

                  return (
                    <div key={groupIdx} className="transcript-paragraph-block">
                      <div className="transcript-speaker-header">
                        <div className="transcript-speaker-avatar">
                          <BsPersonFill />
                        </div>
                        {timeLabel && (
                          <span className="transcript-paragraph-timestamp">{timeLabel}</span>
                        )}
                      </div>
                      {/* Paragraph text indented under avatar */}
                      <div className={`transcript-segment-text ${isPaused ? 'paused' : ''}`}>
                        {segmentGroup.map((seg, segIdx) => {
                          const needsSpace = segIdx > 0;
                          const segText = seg.text?.trim() || '';
                          const prevText = segIdx > 0 ? (segmentGroup[segIdx - 1].text?.trim() || '') : '';
                          const prevEndsWithPunctuation = /[.!?,;:]$/.test(prevText);
                          const currentStartsWithSpace = /^\s/.test(seg.text || '');
                          const shouldAddSpace = needsSpace && !currentStartsWithSpace;
                          return (
                            <span key={segIdx}>
                              {shouldAddSpace && ' '}
                              <span
                                className={`segment-text ${seg.final ? '' : 'text-muted fst-italic'}`}
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(segText, {
                                    ALLOWED_TAGS: [
                                      'p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 
                                      'ol', 'ul', 'li', 'blockquote', 'code', 'pre', 'a', 'img', 'span'
                                    ],
                                    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'target', 'style']
                                  })
                                }}
                              />
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {isPaused && (
                  <div className="text-center mt-4">
                   <div className="d-inline-block px-4 py-2 rounded-pill pause-indicator-badge">
                      <BsPauseCircleFill className="me-2" />
                      <strong>Transcription paused</strong>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
            {/* '+' icon below transcript area to trigger manual text modal (visible during active sessions) */}
          {(webSocketConnectedRef.current || status === '🎙️ Listening...' || status === 'Paused' || isLive) && (
            <div className="w-100 d-flex justify-content-end mb-3 pe-4">
              <button
                className="btn btn-primary rounded-circle manual-text-button"
                title="Add manual text"
                onClick={() => {
                  logger.debug('Manual text modal opened', {
                    component: COMPONENT_NAME,
                    action: 'CLICK_ADD_TEXT',
                    userId: getAuthenticatedUserId(),
                    context: { isLive, isPaused }
                  });
                  setShowManualTextModal(true);
                }}
              >
              <span className="manual-text-button-span">+</span>
              </button>
            </div>
          )}
          {/* ManualTextModal for manual text addition */}
          <ManualTextModal
            show={showManualTextModal}
            onClose={() => setShowManualTextModal(false)}
            onAddText={handleAddManualTextModal}
            timer={timer}
            getNewDivId={getNewDivId}
            authIdToken={authIdToken}
          />

          <ShareModal show={showShareModal} onClose={() => setShowShareModal(false)} shareLink={shareLink} />
          
          <SubscriptionExpiredModal
            show={showSubscriptionExpiredModal}
            onHide={() => {
              setShowSubscriptionExpiredModal(false);
              setSubscriptionModalType('expired');
            }}
            modalType={subscriptionModalType}
          />
        </div>
      </div>


      {/* Loading Modal - Positioned over main container only */}
      {showMicLoadingModal && (
        <div 
          className="mic-loading-modal-overlay"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mic-loading-modal-content">
            <h5 className="mic-loading-modal-title">
              {loadingModalType === 'starting' 
                ? 'Please wait while the microphone starts' 
                : 'Please wait while we process your recording'}
            </h5>
            <div className="mic-loading-modal-subtitle">
              {loadingModalType === 'starting' 
                ? 'Starting microphone' 
                : 'Processing and saving your note'}
            </div>
            <div className="mic-loading-progress-bar">
              <div className="mic-loading-progress-fill" />
            </div>
          </div>
        </div>
      )}

      {/* === BOTTOM BAR === */}
      <div className="position-fixed bottom-0 bg-white shadow-lg bottom-bar-fixed" style={{ left: sidebarWidth, right: 0 }}>
        <div className="container-fluid d-grid" style={{ padding: 0, display: 'grid', placeItems: 'center' }}>
          <div className="d-flex align-items-center justify-content-center bottom-control-container" style={{ maxWidth: 1200, margin: '0 auto', padding: '0 16px', gap: '12px' }}>

            {/* Status Dot Indicator */}
            <div
              className={`status-dot-container ${isReconnectingUi ? 'reconnecting' : 'normal'}`}
              title={isReconnectingUi ? "Reconnecting..." : isPaused ? "Paused" : isLive ? "Recording" : "Ready"}
            >
              <div
                className={`status-dot-inner ${isPaused ? 'paused' : isLive ? 'recording' : 'idle'}`}
              />

              {isReconnectingUi && (
                <div
                  className="spinner-border spinner-border-sm ms-2 reconnection-spinner"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              )}
            </div>

            {/* Play/Pause Button */}
            {isLive && (
              <button
                className="control-button btn d-flex align-items-center justify-content-center control-button-base"
                onClick={() => {
                  isPaused ? handleResume() : handlePause();
                }}
                title={isPaused ? 'Resume recording' : 'Pause recording'}
              >
                {isPaused ? (
                  <BsMicFill style={{ fontSize: '1.3rem', color: '#14375A' }} />
                  ) : (
                  <BsPauseFill style={{ fontSize: '1.5rem', color: '#14375A' }} />
                )}
              </button>
            )}
            {/* Start/Stop Button */}
            {!hideStartButton && (
            <button
              className="control-button btn d-flex align-items-center justify-content-center control-button-base"
              onClick={isLive ? handleStop : handleStart}
              title={isLive ? "Stop recording" : "Start recording"}
            >
              {isLive ? (
                <BsStopFill style={{ fontSize: "1.5rem", color: "#d63333" }} />
              ) : (
                <BsRecordCircleFill style={{ fontSize: "1.8rem", color: "#1A73E8" }} />
              )}
            </button>
          )}

            {/* Waveform Progress Bar */}
            <div className="d-flex align-items-center waveform-container">
              <canvas
                ref={waveformRef}
                width={200}
                height={28}
                className="rounded-3 waveform-canvas"
              />
            </div>
            {/* Timer */}
            <div 
              className={`fw-bold timer-display-base ${isPaused ? 'paused' : isLive ? 'recording' : 'idle'}`}
            >
              {formatTimer(timer)}
            </div>

            {/* Plan limit countdown – visible after the warning toast disappears */}
            {!userInitiatedStopRef.current && !(typeof window !== 'undefined' && window.isStoppingSession) && planCountdownActive && planCountdown !== null && (
              <div
                className={`fw-bold plan-countdown-display ${isPaused ? 'paused' : (planCountdown <= 30 ? 'warning' : 'normal')}`}
                title={isPaused ? 'Countdown paused' : 'Click to see time left'}
                onClick={() => {
                  const left = formatPlanCountdown(planCountdown);
                  showPlanWarningToast(`Your plan will expire in ${left}`, 2000);
                }}
              >
                <BsClockFill style={{ fontSize: '0.85rem', opacity: 0.95 }} />
                {formatPlanCountdown(planCountdown)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Record;